import React from 'react';
import { Gavel, Calendar, FileText, AlertTriangle, Scale, XCircle, CheckCircle, Lightbulb, BookOpen } from 'lucide-react';

export interface Badge {
  text: string;
  type: string;
}

export interface CaseSection {
  id: string;
  title: string;
  icon: any;
  contentType: 'patents' | 'html' | 'quotes';
  content: React.ReactNode | any[];
}

export interface CaseDetail {
  id: string;
  title: string;
  courtInfo: {
    subtitle: string;
    citation: string;
  };
  badges: Badge[];
  pdfLink?: string;
  sections: CaseSection[];
}



const StatusBadge = ({ type, text }) => {
  const colors = {
    ineligible: 'bg-red-100 text-red-800 border-red-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    guidance: 'bg-blue-100 text-blue-800 border-blue-200'
  }

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colors[type]}`}>
      {text}
    </span>
  )
}

export const aiVisualizeCaseDetail: CaseDetail = {
  id: 'fed_cir-2022-2109',
  title: 'AI Visualize, Inc. v. Nuance Communications, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit 2024 • Abstract Idea & Medical Scan Visualization',
    citation: 'AI Visualize, Inc. v. Nuance Commc’ns, Inc., No. 22-2109 (Fed. Cir. Apr. 4, 2024)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Medical Imaging' },
    { type: 'guidance', text: 'Precedential' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/22-2109.OPINION.4-4-2024_2296276.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-400 p-4 rounded-r-lg">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-800">Patent Ineligible - § 101</h3>
            </div>
            <p className="text-red-700">
              The Federal Circuit affirmed dismissal under Rule 12(b)(6), holding all asserted claims ineligible as directed
              to the abstract idea of manipulating and displaying medical imaging data using generic computer components.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-800">Case Details</span>
              </div>
              <div className="space-y-2 text-sm">
                <div><strong>Case No:</strong> 22-2109</div>
                <div><strong>Decided:</strong> April 4, 2024</div>
                <div><strong>Court:</strong> Federal Circuit</div>
                <div><strong>Panel:</strong> Chief Judge Moore, Judges Reyna & Hughes</div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-gray-800">Significance</span>
              </div>
              <div className="space-y-2">
                <StatusBadge type="ineligible" text="Visualization / UI Claims" />
                <StatusBadge type="warning" text="Functional Claiming" />
                <StatusBadge type="guidance" text="Rule 12(b)(6) § 101 Decision" />
              </div>
            </div>
          </div>
        </div>
      )
    },

    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Medical Imaging / Visualization Patents',
          numbers: 'U.S. Patent Nos. 8,701,167 • 9,106,609 • 9,438,667 • 10,930,397',
          description:
            'Directed to systems that provide remote, fast access to advanced visualization of medical scans using a web-based portal and a volume visualization dataset (VVD) stored on a server.',
          claims: [
            'Maintaining a volume visualization dataset (VVD) for medical scans on a server',
            'Receiving user requests at a client and sending parameters to the server',
            'Server-side generation of viewable images from the VVD based on user inputs',
            'Transmitting generated images back to the client for interactive visualization'
          ]
        }
      ]
    },

    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          {/* Step 1 */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to an Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims are directed to the abstract idea of manipulating and displaying medical imaging data.</li>
              <li>• Focus is on the <strong>result</strong> of providing images/visualizations, not a specific technological improvement.</li>
              <li>• Use of a server, client, and network is described at a high, functional level.</li>
              <li>• No concrete improvement to how computers process or render the images themselves.</li>
            </ul>
          </div>

          {/* Step 2 */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Implementation relies on generic server, client, and network components.</li>
              <li>• Steps like "processing," "generating," and "displaying" images are routine computer functions.</li>
              <li>• Offloading processing to a server is treated as a conventional practice.</li>
              <li>• Additional elements do not transform the abstract idea into a patent-eligible application.</li>
            </ul>
          </div>
        </div>
      )
    },

    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* What doesn’t work */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                What Doesn’t Work
              </h3>
              <ul className="space-y-2 text-sm text-red-700">
                <li>• High-level “display / generate / store” imaging claims on generic servers and clients.</li>
                <li>• Functional description of visualization without specific algorithms.</li>
                <li>• Merely moving existing imaging workflows to a web portal.</li>
                <li>• Relying on faster / more convenient access as the “innovation.”</li>
              </ul>
            </div>

            {/* What might work */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                What Might Work
              </h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• New, concrete rendering or reconstruction algorithms for medical scans.</li>
                <li>• Non-conventional data structures or pipelines for handling large imaging datasets.</li>
                <li>• Improvements to computer or network performance tied to specific technical features.</li>
                <li>• Claims that explain <strong>how</strong> the visualization is achieved, not just that it is.</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-3">Drafting Strategies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div className="space-y-2">
                <div><strong>Anchor claims in technical detail:</strong> data structures (e.g., how the VVD is stored/organized), rendering steps, or compression.</div>
                <div><strong>Explain the “how”:</strong> specify steps that differ from conventional imaging systems.</div>
                <div><strong>Avoid pure UI/result language:</strong> tie user interactions to concrete technical processing steps.</div>
              </div>
              <div className="space-y-2">
                <div><strong>Highlight computer improvements:</strong> reduced bandwidth, better memory usage, or faster computation due to specific techniques.</div>
                <div><strong>Show non-generic architecture:</strong> more than a standard server–client over a network.</div>
                <div><strong>Use dependent claims:</strong> to lock in particular algorithms or pipelines that show non-conventional practice.</div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-3">Court’s Message for Visualization Tech</h3>
            <blockquote className="text-purple-700 italic border-l-4 border-purple-400 pl-4">
              Medical imaging and visualization can be important technologies, but claims that merely recite
              manipulating and displaying imaging data using generic servers and clients, without a specific
              technological improvement, are not patent eligible under § 101.
            </blockquote>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Legal Impact</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Confirms strict treatment of functional visualization claims.</li>
                <li>• Reinforces that generic web/portal architectures are conventional.</li>
                <li>• Useful precedent for § 101 motions in imaging/software cases.</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Strategic Implications</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Pushes applicants toward algorithm-level innovations.</li>
                <li>• Encourages detailed technical disclosure at the spec level.</li>
                <li>• Highlights vulnerability of “portal” / SaaS visualization patents.</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Innovation Path</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Novel volume-rendering or reconstruction techniques.</li>
                <li>• Efficient data formats and pipelines for large imaging sets.</li>
                <li>• Hardware-aware optimizations tightly coupled to software.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
};

export const recentiveCaseDetail: CaseDetail = {
  id: 'fed_cir-2023-2437',
  title: 'Recentive Analytics, Inc. v. Fox Corp.',
  courtInfo: {
    subtitle: 'Federal Circuit 2025 • First Major AI/ML Patent Eligibility Precedent',
    citation: 'Recentive Analytics, Inc. v. Fox Corp., No. 2023-2437 (Fed. Cir. Apr. 18, 2025)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'AI/ML' },
    { type: 'warning', text: 'Software' },
    { type: 'guidance', text: 'Landmark Decision' }
  ],
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-400 p-4 rounded-r-lg">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-800">Patent Ineligible - § 101</h3>
            </div>
            <p className="text-red-700">Federal Circuit affirmed dismissal - All four ML patents held ineligible under Alice framework</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-800">Case Details</span>
              </div>
              <div className="space-y-2 text-sm">
                <div><strong>Case No:</strong> 2023-2437</div>
                <div><strong>Decided:</strong> April 18, 2025</div>
                <div><strong>Court:</strong> Federal Circuit</div>
                <div><strong>Panel:</strong> Judges Dyk, Prost, Goldberg</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-gray-800">Significance</span>
              </div>
              <div className="space-y-2">
                <StatusBadge type="warning" text="First Major AI/ML Precedent" />
                <StatusBadge type="ineligible" text="Generic 'Do It With AI' Claims" />
                <StatusBadge type="guidance" text="Framework for AI Patents" />
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Machine Learning Training Patents',
          numbers: '11,386,367 & 11,537,960',
          description: 'Systems for optimizing live event schedules using machine learning...',
          claims: [
            'Collecting event parameters and target features',
            'Iterative training of ML model on historical data',
            'Generating optimized schedules',
            'Real-time updates based on changing conditions'
          ]
        },
        {
          title: 'Network Map Patents',
          numbers: '10,911,811 & 10,958,957',
          description: 'Systems for automatically generating TV broadcast network maps...',
          claims: [
            'Receiving broadcasting schedules',
            'Creating network maps using ML optimization',
            'Real-time updates to broadcast mapping',
            'Determining optimal program placement by market'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Ideas
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Generic ML Technology:</strong> Patents use "any suitable machine learning technique"</li>
              <li>• <strong>No Technological Improvement:</strong> Iterative training is "incident to the very nature of machine learning"</li>
              <li>• <strong>Field of Use Limitation:</strong> Applying existing ML to new environment doesn't escape abstraction</li>
              <li>• <strong>Functional Claiming:</strong> Claims don't describe HOW improvement works, only THAT it occurs</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Restating Abstract Idea:</strong> "Using ML to generate optimized maps and schedules"</li>
              <li>• <strong>Generic Computing:</strong> Only conventional processors and computers</li>
              <li>• <strong>Speed/Efficiency Insufficient:</strong> Faster than humans ≠ patent eligible</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                What Doesn't Work
              </h3>
              <ul className="space-y-2 text-sm text-red-700">
                <li>• Simply claiming "use machine learning" in new field</li>
                <li>• Generic ML techniques without specific improvements</li>
                <li>• Functional claiming without HOW details</li>
                <li>• Speed/efficiency gains alone</li>
                <li>• Iterative training (inherent to ML)</li>
                <li>• Real-time updates (conventional)</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                What Might Work
              </h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Specific improvements to ML models/algorithms</li>
                <li>• Novel computer techniques differing from human approaches</li>
                <li>• Concrete technological solutions to ML problems</li>
                <li>• Detailed implementation showing HOW improvement works</li>
                <li>• Computer-centric functionality improvements</li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-3">Drafting Strategies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div className="space-y-2">
                <div><strong>Focus on Technical Improvements:</strong> Claim specific enhancements to ML algorithms</div>
                <div><strong>Detailed Implementation:</strong> Describe HOW the ML improvement works</div>
                <div><strong>Avoid Generic Language:</strong> Don't claim "any suitable ML technique"</div>
              </div>
              <div className="space-y-2">
                <div><strong>Computer-Centric Claims:</strong> Focus on computer functionality improvements</div>
                <div><strong>Distinguish from Human Methods:</strong> Show fundamentally different computer techniques</div>
                <div><strong>Concrete Solutions:</strong> Provide specific implementation details</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-3">Court's Final Message</h3>
            <blockquote className="text-purple-700 italic border-l-4 border-purple-400 pl-4">
              "Machine learning is a burgeoning and increasingly important field and may lead to patent-eligible improvements in technology. Today, we hold only that patents that do no more than claim the application of generic machine learning to new data environments, without disclosing improvements to the machine learning models to be applied, are patent ineligible under § 101."
            </blockquote>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Legal Impact</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• First major AI/ML precedent</li>
                <li>• Framework for ML evaluation</li>
                <li>• High bar for AI patents</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Strategic Implications</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Focus on algorithmic innovation</li>
                <li>• Trade secret consideration</li>
                <li>• Hardware + software combos</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Innovation Push</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Fundamental ML improvements</li>
                <li>• Novel architectures/methods</li>
                <li>• Concrete tech solutions</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const enfishCaseDetail: CaseDetail = {
  id: 'fed_cir-2015-1244',
  title: 'Enfish, LLC v. Microsoft Corp.',
  courtInfo: {
    subtitle: 'Federal Circuit • May 12, 2016',
    citation: 'Enfish, LLC v. Microsoft Corp., 822 F.3d 1327 (Fed. Cir. May 12, 2016)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Database' },
    { type: 'innovation', text: 'Software/Database Innovation' },
    { type: 'guidance', text: 'Self‑Referential Table' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/15-1244.opinion.5-10-2016.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-400 p-4 rounded-r-lg">
            <div className="flex items-center gap-2 mb-2">
              <Gavel className="w-5 h-5 text-green-700" />
              <h3 className="font-semibold text-green-800">Patent Eligible - § 101</h3>
            </div>
            <p className="text-green-700">
              The Federal Circuit reversed the district court, holding that the claims were not directed to an abstract idea and instead improved computer functionality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-800">Case Details</span>
              </div>
              <div className="space-y-2 text-sm">
                <div><strong>Case No:</strong> 2015-1244</div>
                <div><strong>Decided:</strong> May 12, 2016</div>
                <div><strong>Court:</strong> Federal Circuit</div>
                <div><strong>Panel:</strong> Judges Moore, Taranto, Hughes</div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-gray-800">Significance</span>
              </div>
              <div className="space-y-2">
                <span className="inline-block text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-200">
                  Improves database structure
                </span>
                <span className="inline-block text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full border border-blue-200">
                  Key § 101 software eligibility case
                </span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Self‑Referential Database Patents',
          numbers: '6,151,604 & 6,163,775',
          description: 'Logical model storing all data entities in a single self‑referential table for faster lookup and flexibility.',
          claims: [
            'Configuring memory according to a logical table with self‑referential structure',
            'Using object IDs both as rows and column identifiers',
            'Indexing data within the logical table'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Gavel className="w-5 h-5" />
              Step 1: Not Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims are directed to "a specific improvement to the way computers operate"</li>
              <li>• The self-referential table improves data storage and retrieval</li>
              <li>• Not simply using a computer to perform a task faster, but changing the underlying data structure</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"The claims are directed to a specific improvement to computer functionality itself, not on economic or other tasks."',
        '"We do not read Alice to broadly hold that all improvements in computer‑related technology are inherently abstract."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <Gavel className="w-5 h-5" />
                What Worked
              </h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Specific improvement to database architecture</li>
                <li>• Claimed data model improved computer operation itself</li>
                <li>• Avoided functional/generalized claiming</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                What to Watch
              </h3>
              <ul className="space-y-2 text-sm text-yellow-700">
                <li>• Ensure technical nature of improvement is clear</li>
                <li>• Explain how claims differ from prior data storage techniques</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-3">Court’s Final Message</h3>
            <blockquote className="text-purple-700 italic border-l-4 border-purple-400 pl-4">
              "We do not read Alice to broadly hold that all improvements in computer-related technology are inherently abstract... software can make non-abstract improvements to computer technology."
            </blockquote>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Legal Impact</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Landmark eligibility case for software</li>
                <li>• Helped clarify Step 1 of Alice</li>
                <li>• Often cited for tech-specific improvements</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Strategic Implications</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Show technical improvement clearly</li>
                <li>• Avoid claiming ends/results only</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Innovation Push</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• Supports eligible software patents</li>
                <li>• Focus on how computer operation improves</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const mcroCaseDetail: CaseDetail = {
  id: 'fed_cir-2015-1080',
  title: 'McRO, Inc. v. Bandai Namco Games America, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • Sept 13, 2016',
    citation: 'McRO, Inc. v. Bandai Namco Games Am. Inc., 837 F.3d 1299 (Fed. Cir. Sept. 13, 2016)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Algorithm' },
    { type: 'innovation', text: 'Rules‑Based Animation' },
    { type: 'guidance', text: 'Technological Improvement' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/s15-1080.opinion.9-9-2016.2.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit reversed a Rule 12(c) dismissal, holding that the claimed rule‑based method for phoneme-based lip‑sync animation is patent eligible because it provides a technological improvement over traditional manual keyframe animation.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Case Details</span>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Case No:</strong> 2015‑1080</div>
              <div><strong>Decided:</strong> September 13, 2016</div>
              <div><strong>Court:</strong> Federal Circuit (Reyna, Taranto, Stoll)</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Applet Lip‑Syncing Patents',
          numbers: '6,307,576 & 6,611,278',
          description: 'Rule‑based method for automatic lip-synchronization and facial expression of 3D characters.',
          claims: [
            'Obtain timed phoneme transcript and rules',
            'Generate morph weight streams via specific rules',
            'Apply morph weights to animate character'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Gavel className="w-5 h-5" />
              Step 1: Not Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims recite a specific set of rules for generating facial animation based on phonemes</li>
              <li>• The rules are not merely functional but structured as an ordered combination of steps</li>
              <li>• Automation here is not abstract—it improves animation by removing subjectivity from manual techniques</li>
              <li>• Claims do not preempt all lip-sync animation; they're limited to a genus of rule-based processes</li>
              <li>• Therefore, the court resolved eligibility at Step 1 without reaching Step 2</li>
            </ul>
          </div>
          <p className="text-sm">
            The Federal Circuit held that the claims are <strong>not</strong> directed to an abstract idea under Alice Step 1.
            Unlike generic automation, these claims recite an <strong>ordered combination</strong> of <strong>specific rules</strong>
            that process phoneme subsequences and generate morph‑weight streams—constituting a <strong>technological improvement</strong>
            over traditional animation techniques. See <em>McRO, Inc. v. Bandai Namco Games Am. Inc.</em>, 837 F.3d 1299, 1315 (Fed. Cir. 2016).
          </p>
          <p className="text-sm">
            The court emphasized that the rules are a <strong>“genus” of unconventional rules</strong>, not mere generic functional steps,
            thus avoiding <strong>preemption</strong> of all forms of lip-sync automation.
          </p>
          <p className="text-sm">
            The decision illustrates how <strong>claim specificity and concrete implementation</strong> can satisfy eligibility under Alice Step 1
            even when implemented in software.
          </p>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"The ordered combination of claimed steps … is not directed to an abstract idea and is therefore patent‑eligible subject matter under § 101."',
        '"The specific structure of the claimed rules would prevent broad preemption of all rules‑based means of automating lip synchronization."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">What Worked</h3>
            <ul className="text-sm space-y-2 text-green-700">
              <li>Claim construction was central—define rules narrowly</li>
              <li>Emphasize concrete ordered combination of steps</li>
              <li>Provide evidence that claims do not preempt all variations</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">What to Watch</h3>
            <ul className="text-sm space-y-2 text-yellow-700">
              <li>Avoid overgeneralizing the rule‑genus in claims</li>
              <li>Support record with evidence distinguishing from manual methods</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic pl-4 border-l-4 border-purple-400 text-purple-700">
            “Processes that automate tasks that humans are capable of performing are patent eligible if properly claimed.”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>4th major Federal Circuit win on software eligibility post‑Alice</li>
                <li>Reinforces importance of preemption analysis at Step 1</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Claim specific technological improvements, not abstract results</li>
                <li>Use evidence on lack of preemption in prosecution and litigation</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Innovation Push</h4>
              <ul className="space-y-1">
                <li>Supports software patents claiming rule-based methods</li>
                <li>Catalyzes precise claim drafting around technology improvements</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const ddrCaseDetail: CaseDetail = {
  id: 'fed_cir-2013-1505',
  title: 'DDR Holdings, LLC v. Hotels.com, L.P.',
  courtInfo: {
    subtitle: 'Federal Circuit • Dec 5, 2014',
    citation: 'DDR Holdings, LLC v. Hotels.com, L.P., 773 F.3d 1245 (Fed. Cir. Dec. 5, 2014)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'innovation', text: 'Internet‑Specific Solution' },
    { type: 'guidance', text: 'Rooted in Computer Tech' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/13-1505.opinion.12-3-2014.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            DDR Holdings innovated methods for generating hybrid webpages that preserve a host website’s “look and feel” while embedding commerce content, to prevent loss of visitor traffic—a problem rooted in Internet technology, not abstract business logic.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Case Details</span>
            </div>
            <div className="space-y-2">
              <div><strong>Case No:</strong> 2013‑1505</div>
              <div><strong>Decided:</strong> December 5, 2014</div>
              <div><strong>Court:</strong> Federal Circuit (Lourie, Bryson, Wallach)</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 1: Directed to Computer-Specific Solution
            </h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• The claims addressed a problem **specific to Internet functionality**—merchant traffic loss—and proposed a technical solution to prevent that loss</li>
              <li>• The method produced a **composite web page** preserving the host site's “look and feel” while delivering merchant content.</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 2: Inventive Concept Present
            </h3>
            <p className="text-sm text-gray-700">
              Because the claims are rooted in computer technology rather than abstract ideas or business methods, the court found that the inventive concept inquiry is satisfied and **Alice Step 2 is moot**.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">⚙️ What Worked</h3>
            <ul className="space-y-2">
              <li>Frame the invention as solving a **problem specific to computer networks**.</li>
              <li>Show **how** the technology differs from conventional webpage linking—e.g., creating **composite pages with host styling**.</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">🚧 What to Watch</h3>
            <ul className="space-y-2">
              <li>Don't generalize—the court rejected claims that merely repurpose business practices via Internet use.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <blockquote className="italic pl-4 border-l-4 border-purple-400">
            “The claimed solution is necessarily rooted in computer technology in order to overcome a problem specifically arising in the realm of computer networks.”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>✔ First post-Alice decision affirming software eligibility via Internet-specific technical solution.</li>
                <li>✔ Established exception for inventions addressing network-centric problems.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Use language like “rooted in computer technology” to frame claims.</li>
                <li>Distinguish from generic automation or mere business tasks.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const visualMemoryCaseDetail: CaseDetail = {
  id: 'fed_cir-2016-2254',
  title: 'Visual Memory LLC v. NVIDIA Corp.',
  courtInfo: {
    subtitle: 'Federal Circuit • Aug 15, 2017',
    citation: 'Visual Memory LLC v. NVIDIA Corp., 867 F.3d 1253 (Fed. Cir. Aug 15, 2017)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Hardware' },
    { type: 'warning', text: 'Software' },
    { type: 'innovation', text: 'Programmable Memory System' },
    { type: 'guidance', text: 'Hardware‑Software Improvement' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/16-2254.opinion.8-11-2017.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit reversed the district court’s dismissal under Rule 12, finding that claims to patent‑eligible subject matter because they recite a **programmatically configurable memory system** that adapts based on processor type.
          </p>
          <p className="text-sm">
            The ’740 patent describes a three‑tier memory hierarchy—including main memory and caches—with **programmable operational characteristics** that optimize cache behavior for different processors without compromising performance.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Programmable Memory System Patent',
          numbers: '5,953,740',
          description: 'Memory system configurable by processor type via programmable operational characteristics.',
          claims: [
            'A memory system connectable to a processor with programmable operational characteristics',
            'Programmable main memory and cache connected via bus',
            'Configuring cache behavior based on processor type'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 1: Directed to Technical Improvement
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>Claims focus on a **programmable memory system** tied to processor-configurable behavior—not abstract data storage.</li>
              <li>New memory architecture enables **interoperability across processors** without performance loss, eliminating generic “one‑size‑fits‑all” designs.</li>
              <li>Cites **Enfish and Thales** as precedents: these claims are technological improvements, not abstract ideas.</li>
            </ul>
          </div>
          <p className="text-sm">
            Since eligibility was resolved at Step 1, the Federal Circuit did not proceed to Step 2. The patent was found to describe a **concrete computer memory improvement**, not a generic computer tool.
          </p>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Claims are directed to an improved computer memory system, not the abstract idea of categorical data storage."',
        '"The ’740 patent teaches benefits such as interoperability across processors and better performance without larger caches."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">What Worked</h3>
            <ul className="space-y-2 text-sm text-green-700">
              <li>Claim a **technical improvement** to hardware/software architecture.</li>
              <li>Highlight **configurable system components** tied to processor adaptation.</li>
              <li>Point to **specification benefits**—e.g., interoperability and performance gains.</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">Lessons Learned</h3>
            <ul className="space-y-2 text-sm text-yellow-700">
              <li>Don’t rely on generic architecture; specify **programmable structural change**.</li>
              <li>Support architectural claims with clear **specification examples**.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic border-l-4 border-purple-400 pl-4 text-purple-700">
            “Our review demonstrates that the claims are directed to a technological improvement: an enhanced computer memory system…”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>Fourth major post-Alice Federal Circuit decision confirming Step 1 eligibility.</li>
                <li>Reinforces memory architecture as a technical improvement category.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Patent claims should emphasize **configurable hardware/software structures**.</li>
                <li>Support eligibility with detailed **specification disclosures** and performance data.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Innovation Push</h4>
              <ul className="space-y-1">
                <li>Encourages innovation in **programmable system architecture**, beyond abstract implementations.</li>
                <li>Includes hardware/software synergy as a valuable patent-eligible concept.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const finjanCaseDetail: CaseDetail = {
  id: 'fed_cir-2016-2520',
  title: 'Finjan, Inc. v. Blue Coat Systems, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • Jan 10, 2018',
    citation: 'Finjan, Inc. v. Blue Coat Systems, Inc., 879 F.3d 1299 (Fed. Cir. Jan. 10, 2018)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'innovation', text: 'Virus‑Screening Profile' },
    { type: 'guidance', text: 'Behavior‑Based Improvement' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/16-2520.opinion.1-8-2018.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            Finjan’s ’844 patent—covering attaching a behavior‑based security profile to a downloadable—was held patent‑eligible under § 101. The Federal Circuit affirmed that this specific computer‑implemented security measure is more than an abstract idea. A jury also awarded Finjan $39.5 million on infringement grounds.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Case Details</span>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Patent No:</strong> 6,154,844</div>
              <div><strong>Result:</strong> Eligible under § 101; Infringement affirmed (’844, ’731, ’633)<br />(’968 non‑infringement) </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Security Profile Patent',
          numbers: '6,154,844',
          description: 'Method for generating and linking a behavior‑based security profile to a downloadable executable.',
          claims: [
            'Receive a downloadable via an inspector',
            'Generate a security profile identifying suspicious code',
            'Attach the profile to the downloadable before distribution'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 1: Eligible – Technological Focus
            </h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>The claims improve computer security by profiling downloadables—this is a **specific, non-abstract technical improvement**, not a generic idea.</li>
              <li>Linking the profile adds a **concrete structural change**—not just automation—but a new file type serving a security function.</li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 2: Details Not Needed
            </h3>
            <p className="text-sm">
              Because the court found the claims not directed to an abstract idea at Step 1, it did not reach Alice Step 2.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Behavior‑based virus scans are an improvement to computer functionality."',
        '"The claims employ a new kind of file that enables a computer security system to do things it could not do before."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">What Worked</h3>
            <ul className="text-sm space-y-2 text-green-700">
              <li>Detail the **specific file type and linking mechanism**, not just generic data inspection.</li>
              <li>Show **real-world security effect**, not mere speed or automation.</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">Watch Points</h3>
            <ul className="text-sm space-y-2 text-yellow-700">
              <li>Avoid claims that merely perform virus scanning generically.</li>
              <li>Emphasize the **attachment step** and its resulting computer functionality.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic pl-4 border-l-4 border-purple-400 text-purple-700">
            “We find no error in the district court’s subject matter eligibility determination … [the patent] provides a specific computer security improvement.”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>Paves path for security‑improving software patents post‑Alice</li>
                <li>Premise that attaching profiles confers technological advance</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Assert non‑generic elements early in prosecution</li>
                <li>Align claim language to functionality improvements</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Innovation Push</h4>
              <ul className="space-y-1">
                <li>Validates behavior‑based security innovations</li>
                <li>Requires linkage to computer functionality, not just abstract logic</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const thalesCaseDetail: CaseDetail = {
  id: 'fed_cir-2015-5150',
  title: 'Thales Visionix, Inc. v. United States',
  courtInfo: {
    subtitle: 'Federal Circuit • Mar 8, 2017',
    citation: 'Thales Visionix, Inc. v. United States, 849 F.3d 1310 (Fed. Cir. Mar. 8, 2017)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Hardware' },
    { type: 'innovation', text: 'Inertial Tracking System' },
    { type: 'guidance', text: 'Physics‑Based Configuration' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/15-5150.opinion.3-6-2017.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: null
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Inertial Sensor Configuration Patent',
          numbers: '6,474,159',
          description: 'System for tracking motion of an object relative to a moving reference frame using dual inertial sensors.',
          claims: [
            'First inertial sensor mounted on tracked object',
            'Second inertial sensor mounted on moving reference frame',
            'Determining orientation of object relative to frame using both sensors'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <Gavel className="w-5 h-5" />
              Step 1: Not Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims recite a specific configuration of inertial sensors used to track motion relative to a moving platform</li>
              <li>• Unlike generic mathematical formulas, the system improves tracking precision in real-world applications</li>
              <li>• The invention is tied to a physical system with defined sensor placement and interaction</li>
            </ul>
          </div>

          <p className="text-sm">
            The Federal Circuit found that the claims were not abstract under Alice Step 1 because they describe a concrete physical configuration
            of sensors and a method of calculating orientation using relative motion—an approach rooted in physics and engineering.
          </p>

          <p className="text-sm">
            Importantly, the use of mathematical equations does not render a claim abstract when those equations are applied in a
            defined and innovative technological system. The court likened this to the ruling in <em>Diamond v. Diehr</em>.
          </p>

          <p className="text-sm">
            Since the claims were directed to a specific improvement in tracking systems and not a disembodied mathematical concept,
            the court did not proceed to Alice Step 2.
          </p>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Claims specify a particular configuration of sensors and their use, not just abstract math."',
        '"This is nearly indistinguishable from Diamond v. Diehr, using equations but in a specific system."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">What Worked</h3>
            <ul className="text-sm text-green-700 space-y-2">
              <li>• Tied claims directly to physical hardware: sensor arrangement and function were specifically claimed</li>
              <li>• Framed the innovation as a solution to a technical problem in tracking systems</li>
              <li>• Used math within a specific, practical application—distinguishable from claiming math itself</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">Tips for Drafting</h3>
            <ul className="text-sm text-yellow-700 space-y-2">
              <li>• Avoid generic mathematical descriptions—tie computations to physical systems</li>
              <li>• Specify sensor configurations or system components and how they interact</li>
              <li>• Show that the invention cannot be performed mentally or with pen and paper</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic text-purple-700 border-l-4 border-purple-400 pl-4">
            “A system that applies physics-based equations in a concrete, sensor-based implementation is not abstract—this is what patent law encourages.”
          </blockquote>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>• Reinforces that claims involving math can be eligible when applied in a specific system</li>
                <li>• Emphasized distinction between abstract math and real-world sensor-based applications</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>• Useful precedent for defense of patents involving physics, sensors, or control systems</li>
                <li>• Encourages precise structural claim limitations tied to function</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Technology Relevance</h4>
              <ul className="space-y-1">
                <li>• Strong support for patents in aerospace, navigation, and sensor‑driven fields</li>
                <li>• Demonstrates patent eligibility for embedded systems with novel configuration</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const coreWirelessCaseDetail: CaseDetail = {
  id: 'fed_cir-2016-2684',
  title: 'Core Wireless Licensing S.A.R.L. v. LG Electronics, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • Jan 25, 2018',
    citation: 'Core Wireless Licensing S.A.R.L. v. LG Electronics, Inc., 880 F.3d 1356 (Fed. Cir. Jan. 25, 2018)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'UI/GUI' },
    { type: 'innovation', text: 'Improved UI for Small Screens' },
    { type: 'guidance', text: 'App Summary Window' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/16-2684.opinion.1-23-2018.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed the district court’s denial of LG’s motion for summary judgment under § 101.
            The patents at issue improved user interfaces for small-screen devices by providing an <strong>application summary window</strong> that displayed key functions and data without launching the full application.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">Key Holding</h3>
            <p className="text-sm text-gray-700">
              The claims are <strong>not directed to an abstract idea</strong>, but rather to a specific improvement in computer functionality for constrained UI environments.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'App Summary Window Patents',
          numbers: 'U.S. Patent Nos. 8,713,476 & 8,434,020',
          description: 'Systems improving user experience on small‑screen devices by presenting an application summary window from a main menu.',
          claims: [
            'Display application summary window reachable directly from main menu',
            'Summary shows shortcut list of frequent functions/data',
            'Summary displayed while app remains un‑launched'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Step 1: Not Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims improve the functionality of computing devices by presenting data more efficiently</li>
              <li>• Summary window offers user-relevant information without launching the app</li>
              <li>• Improves interaction for small-screen environments, not a conventional display of information</li>
            </ul>
          </div>
          <p className="text-sm">
            The court found it unnecessary to reach Alice Step 2 because the claims were not abstract under Step 1.
            The invention provided a concrete UI advancement and a better user experience.
          </p>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Claims not directed to abstract idea but to improved user interface for small‑screen devices."',
        '"Application summary window presents data without launching the full application."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Drafting Tips</h3>
            <ul className="text-sm space-y-2 text-blue-700">
              <li>• Clearly describe how the interface improves device functionality</li>
              <li>• Avoid claims that mimic conventional display or content organization</li>
              <li>• Highlight UX constraints and how your invention overcomes them</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic pl-4 border-l-4 border-purple-400 text-purple-700">
            “The claimed invention improves the efficiency of using a computer by improving the interface itself—not just presenting data faster.”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>• Strengthens eligibility for UI improvements</li>
                <li>• Reinforces that usability enhancements are not per se abstract</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategy Notes</h4>
              <ul className="space-y-1">
                <li>• Tie UI behavior directly to technical solutions</li>
                <li>• Emphasize display logic over content delivery</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Innovation Focus</h4>
              <ul className="space-y-1">
                <li>• Small-screen navigation</li>
                <li>• Intuitive access to app functions</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const electricPowerCaseDetail: CaseDetail = {
  id: 'fed_cir-2015-1778',
  title: 'Electric Power Group v. Alstom (2016)',
  courtInfo: {
    subtitle: 'Federal Circuit • July 28, 2016',
    citation: 'Electric Power Group, LLC v. Alstom S.A., 830 F.3d 1350 (Fed. Cir. 2016)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'warning', text: 'Data Collection & Display' },
    { type: 'guidance', text: 'Monitoring Abstracted' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/15-1778.opinion.7-28-2016.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed the district court’s ruling that claims directed to collecting, analyzing, and displaying
            electric grid data were patent ineligible under § 101. The court held that simply gathering and presenting information,
            even in real-time, does not transform an abstract idea into a patent-eligible invention.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm">
            <p><strong>Holding:</strong> Claims directed to data analysis and display for electric grid monitoring are abstract and lack an inventive concept.</p>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Claimed Technology',
      icon: FileText,
      contentType: 'html',
      content: (
        <div className="text-sm space-y-2">
          <p>
            The patents covered methods for receiving large volumes of real-time data from electric power transmission networks,
            analyzing the data to detect events or conditions, and displaying the results on a monitor or interface.
          </p>
          <ul className="list-disc list-inside text-gray-700">
            <li>Receive data from grid sensors (e.g., phasor measurements)</li>
            <li>Analyze signal quality and detect abnormalities</li>
            <li>Display results to grid operators via dashboards or alarms</li>
          </ul>
        </div>
      )
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6 text-sm">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-2">Step 1: Directed to Abstract Idea</h3>
            <ul className="list-disc list-inside text-gray-700">
              <li>Collecting, analyzing, and displaying information are abstract activities</li>
              <li>No improvement to computer functionality or technical innovation</li>
              <li>Focus on information itself rather than any inventive processing technique</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-2">Step 2: No Inventive Concept</h3>
            <ul className="list-disc list-inside text-gray-700">
              <li>Use of generic computing components (e.g., displays, processors)</li>
              <li>No specific algorithms or novel processing steps disclosed</li>
              <li>Presentation of results adds nothing inventive to the abstract idea</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Court’s Observations',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"The focus of the claims... is on collecting information, analyzing it, and displaying certain results of the collection and analysis."`,
        `"Information as such is an intangible."`,
        `"The claims do not even require a new source or type of information, or new techniques for analyzing it."`,
        `"Merely presenting the results... without more, is abstract."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 mb-2">What Doesn’t Work</h4>
            <ul className="list-disc list-inside text-red-700">
              <li>Collecting and displaying data without transforming it</li>
              <li>Functional claiming without a specific improvement</li>
              <li>Generic use of computers or monitors</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-800 mb-2">What Might Work</h4>
            <ul className="list-disc list-inside text-green-700">
              <li>Claims that recite specific technical solutions (e.g., algorithms)</li>
              <li>Detailing how data is processed or transformed</li>
              <li>Limiting claims to particular hardware configurations or real-time system constraints</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <blockquote className="italic text-purple-700 border-l-4 border-purple-400 pl-4">
            "Merely collecting information and displaying it... does not transform the nature of the claim into a patent-eligible application."
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="list-disc list-inside text-gray-600">
                <li>Clarifies limits on data-centric claims under § 101</li>
                <li>Applies and expands on precedent like Mayo and Alice</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategy</h4>
              <ul className="list-disc list-inside text-gray-600">
                <li>Draft claims that go beyond analysis and display</li>
                <li>Include specific data processing steps or hardware use</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Technology Implications</h4>
              <ul className="list-disc list-inside text-gray-600">
                <li>Raises the bar for real-time monitoring systems</li>
                <li>Requires demonstrable technical improvements</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const digitechCaseDetail: CaseDetail = {
  id: 'fed_cir-2013-1600',
  title: 'Digitech Image Technologies LLC v. Electronics for Imaging Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • July 11, 2014',
    citation: '758 F.3d 1344 (Fed. Cir. July 11, 2014)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Data Manipulation Only' },
    { type: 'guidance', text: 'Abstract Idea of Device Profile' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/13-1600.opinion.7-9-2014.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed § 101 ineligibility for claims directed to creating a “device profile” that combines chromatic and spatial data to characterize imaging devices. The court held that this was an abstract idea because it merely organized information through mathematical correlations without more—no new devices, no technological improvement.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Device Profile Data Patent',
          numbers: 'U.S. Patent No. 6,128,415 (claims 1 & 26)',
          description: 'Claims directed to a “device profile” that combines color and spatial data to characterize imaging devices.',
          claims: [
            'Collecting chromatic and spatial transformation data sets',
            'Generating and storing a device profile combining both data sets'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold text-red-800">Step 1: Directed to Abstract Idea</h3>
          <p className="text-sm">
            The court determined the claims were directed to the abstract idea of organizing and manipulating information—specifically, generating device profiles using known mathematical correlations. This falls squarely within the abstract idea exception.
          </p>
          <h3 className="font-semibold text-red-800">Step 2: No Inventive Concept</h3>
          <p className="text-sm">
            The additional claim elements (e.g., obtaining data sets, storing profiles) were all conventional, routine computer activities with no inventive concept to transform the abstract idea into a patent-eligible invention.
          </p>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"The focus of the asserted claims … is on collecting information, analyzing it, and displaying certain results…"',
        '"Nothing in the claims … requires anything other than off‑the‑shelf, conventional computer, network, and display technology."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            Avoid claiming mere data gathering or manipulation. To survive § 101 scrutiny, ensure claims include specific technological improvements—new devices, architectures, or unconventional processing steps—not just organizing or displaying information.
          </p>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            **Digitech** stands as a clear precedent that device profiling via data combination and storage—without technological enhancement—is ineligible. This case became a touchstone in USPTO guidance, often cited under 2106.04(a)(2).
          </p>
        </div>
      )
    }
  ]
}

export const smartSystemsCaseDetail: CaseDetail = {
  id: 'fed_cir-2016-1233',
  title: 'Smart Systems Innovations LLC v. Chicago Transit Authority (2017)',
  courtInfo: {
    subtitle: 'Federal Circuit • Oct 18, 2017',
    citation: 'Smart Systems Innovations LLC v. Chicago Transit Auth., 873 F.3d 1364 (Fed. Cir. Oct. 18, 2017)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Business Method' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Open‑Pay Bankcard' },
    { type: 'guidance', text: 'Payment Method Only' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/16-1233.opinion.10-13-2017.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed an Alice § 101 dismissal for patents covering an open-payment transit fare system using bankcards (e.g., Ventra). The claimed inventions involve scanning a bankcard, comparing it to a white list of valid accounts, and allowing access if approved—even to support multiple balances and time-based passes.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Case Details</span>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Case No:</strong> 16‑1233</div>
              <div><strong>Decided:</strong> October 18, 2017</div>
              <div><strong>Court:</strong> Federal Circuit (Reyna, Linn, Wallach)</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Bankcard Transit Payment Patents',
          numbers: '(e.g., US Patents ’003, ’617, ’816, ’390)',
          description: 'Systems enabling use of bankcards to pay fares at transit gates, even when offline, by comparing card-scanned data to locally stored account info.',
          claims: [
            'Scan bankcard at terminal',
            'Compare scanned data to local white list',
            'Authorize or deny gate entry',
            'Support multiple balance types or time‑based fares'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>Claims involve financial transactions—paying with a bankcard—which the court deemed abstract.</li>
              <li>No claim to improved hardware, new card, or novel data processing methods—only generic computer components.</li>
              <li>Limiting to transit or offline payments doesn’t avoid abstraction—still an abstract idea even if field‑specific.</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>Claims add only routine computer elements—processor, memory, interfaces.</li>
              <li>No inventive concept as in Diehr, DDR, or Enfish since there’s no tech improvement.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Paying for a subway or bus ride with a credit card" is an abstract idea.',
        '"Nothing in the claims … requires anything other than off‑the‑shelf, conventional computer…"'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">What Didn’t Help</h3>
            <ul className="space-y-2 text-sm text-yellow-700">
              <li>Field-specific context (transit system) alone isn’t enough.</li>
              <li>Generic use of computers and data storage doesn’t add inventive concept.</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">What To Focus On</h3>
            <ul className="space-y-2 text-sm text-green-700">
              <li>Emphasize technological improvements to improve § 101 chances.</li>
              <li>Claim novel devices, data structures, or protocols—not just use cases.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <blockquote className="italic pl-4 border-l-4 border-purple-400 text-purple-700">
            “The claimed inventions are directed to the collection, analysis, and classification of information via generic computer components—not patent‑eligible.”
          </blockquote>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>Reinforced that financial-data claims remain abstract under § 101.</li>
                <li>Confirmed Enfish/DDR exceptions require actual tech improvements.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Draft claims emphasizing novel technical elements, not outcomes.</li>
                <li>Avoid relying on “field specificity” alone in § 101 defense.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Innovation Push</h4>
              <ul className="space-y-1">
                <li>Encourage clear focus on technological advances in specs/specs and claims.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const chargePointCaseDetail: CaseDetail = {
  id: 'fed_cir-2018-1739',
  title: 'ChargePoint, Inc. v. SemaConnect, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • Mar 28, 2019',
    citation: 'ChargePoint, Inc. v. SemaConnect, Inc., 920 F.3d 759 (Fed. Cir. Mar. 28, 2019)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Hardware' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'warning', text: 'EV Charging + Network' },
    { type: 'guidance', text: 'Abstract Idea: Remote Control' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/18-1739.opinion.3-28-2019.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <p>
            The Federal Circuit affirmed the district court's dismissal under § 101, holding that ChargePoint’s patents—
            which relate to networked electric vehicle (EV) charging stations—were directed to the abstract idea of
            remotely controlling charging through network communications.
          </p>
          <p>
            Despite their grounding in physical components, the court found the claims abstract because the focus was on
            “network control,” a concept lacking a technological solution or inventive concept. No improvements to EV
            charging hardware or software were described beyond routine computer and network functions.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Network‑Controlled EV Station Patents',
          numbers: 'U.S. Patent Nos. 8,138,715; 8,432,131; 8,450,967; 7,956,570',
          description: 'Systems where network or server remotely manage power flow, station availability, billing, and demand response.',
          claims: [
            'Apparatus comprising station, transceiver communicating with remote server, controller enabling charge flow',
            'Method receiving request, determining enablement, sending communication to start/stop charging',
            'Modifying electricity flow using demand-response signals'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Network Communication:</strong> Claims focus on the abstract idea of controlling devices remotely over a network</li>
              <li>• <strong>No Specific Technological Solution:</strong> The patents describe results (controlling charging) but not how the technology is improved</li>
              <li>• <strong>Functional Claiming:</strong> Use of terms like “communicate” or “enable” lacks technical specificity</li>
              <li>• <strong>Broad Conceptual Framing:</strong> Field-of-use (EV charging) does not confer eligibility to otherwise abstract claims</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Generic Components:</strong> The system uses conventional elements like transceivers and controllers in expected ways</li>
              <li>• <strong>Abstract Idea Itself:</strong> The asserted “innovation” was remote network control—which is the abstract idea itself</li>
              <li>• <strong>No Technical Improvement:</strong> Nothing in the claims transformed the underlying technology of EV charging systems</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"The focus of the asserted claims … is on the abstract idea of communicating over a network for device interaction."',
        '"Network control is the abstract idea itself … and the claim’s use of it cannot supply the inventive concept."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6 text-sm">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-2">What Doesn’t Work</h3>
            <ul className="list-disc ml-5 text-red-700 space-y-1">
              <li>Claiming generic functionality like remote enablement or control</li>
              <li>Relying on field-of-use (e.g. EV stations) without technical innovation</li>
              <li>No details on how hardware behavior is changed or improved</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <p>
            <strong>Legal Impact:</strong> Reinforces that implementing an abstract idea in a specific field—
            even in emerging areas like EV infrastructure—does not confer eligibility.
          </p>
          <p>
            <strong>Strategic Insight:</strong> Patent claims must go beyond using a network to control devices;
            there must be a technical solution to a technical problem.
          </p>
          <p>
            <strong>Drafting Lesson:</strong> Emphasize how the invention modifies system behavior,
            not just the effect of using conventional tools (e.g., servers, controllers).
          </p>
        </div>
      )
    }
  ]
}

export const twoWayMediaCaseDetail: CaseDetail = {
  id: 'fed_cir-2016-2531',
  title: 'Two‑Way Media Ltd. v. Comcast Cable Communs.',
  courtInfo: {
    subtitle: 'Federal Circuit • Nov 1, 2017',
    citation: 'Two‑Way Media Ltd. v. Comcast Cable Communs., 874 F.3d 1329 (Fed. Cir. Nov 1, 2017)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'warning', text: 'Internet Multicasting' },
    { type: 'guidance', text: 'Abstract Idea: Streaming Data' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/16-2531.opinion.10-27-2017.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed a Rule 12(c) dismissal, holding that claims directed to multi‑casting media streams,
            monitoring delivery, and logging usage are ineligible under § 101 because they are directed to the abstract idea
            of controlling streaming data using generic network components.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800">Case Details</span>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Case No:</strong> 2016‑2531</div>
              <div><strong>Decided:</strong> November 1, 2017</div>
              <div><strong>Court:</strong> Federal Circuit (Lourie, Reyna, Hughes)</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Internet Multicasting Patents',
          numbers: 'U.S. Patents 5,778,187; 5,983,005; 6,188,622; 6,208,686',
          description: 'Methods for streaming audio/video, monitoring delivery, routing, and logging over the Internet.',
          claims: [
            'Selecting an intermediate server to forward a real‑time media stream to a user',
            'Detecting when delivery stops, then logging the delivered portion',
            'Routing media stream packets and accumulating delivery data for reporting'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• The court found claims centered on “selecting a streaming server, forwarding data, monitoring delivery, and logging”—an abstract idea of multicasting and tracking digital media.</li>
              <li>• Although reciting network steps, nothing required specific improvements to network technology or unconventional hardware.</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• All additional elements involved generic Internet and server technology performing standard functions.</li>
              <li>• There was nothing in the claims that integrated the abstract idea into a novel or improved technological implementation.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Sending and monitoring the delivery of audio/visual information" = abstract idea.',
        '"Claims directed to abstract idea where ... network components are conventional."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-2">What Went Wrong</h3>
            <ul className="text-sm space-y-2 text-red-700">
              <li>• Reciting generic network functions (forward, monitor, log) isn’t enough; need specificity on how the network is improved.</li>
              <li>• Merely logging or monitoring without altering underlying architecture does not add inventive concept.</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">What to Watch</h3>
            <ul className="text-sm space-y-2 text-yellow-700">
              <li>Avoid drafting claims purely in terms of data flow or abstract steps.</li>
              <li>Highlight any novel server configurations, message formats, or improved delivery methods in the claim language.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm italic">
            “Two‑Way Media reinforces the principle that claims directed to managing or tracking information on the Internet,
            without inventive integration, are abstract and § 101-ineligible.”
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <h4 className="font-semibold mb-1">Legal Impact</h4>
              <ul className="space-y-1">
                <li>Affirms that Internet streaming control is abstract absent enabling improvement.</li>
                <li>Extends the abstract‑idea boundary to include media-data monitoring claims.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Strategic Implications</h4>
              <ul className="space-y-1">
                <li>Claim improvements to the *network architecture*, not just functions.</li>
                <li>Use technical specifics—protocols, formats, system metrics—to undercut abstract characterization.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Innovation Push</h4>
              <ul className="space-y-1">
                <li>Encourages inventors to emphasize bespoke streaming protocols or monitoring architectures.</li>
                <li>Discourages reliance on conventional delivery systems in claim language.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]
}

export const tliCommunicationsCaseDetail: CaseDetail = {
  id: 'fed_cir-2015-1372',
  title: 'In re TLI Communications LLC (2016)',
  courtInfo: {
    subtitle: 'Federal Circuit • May 17, 2016',
    citation: 'In re TLI Communications LLC, 823 F.3d 607 (Fed. Cir. May 17, 2016)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Network/Internet' },
    { type: 'warning', text: 'Image Upload & Classification' },
    { type: 'guidance', text: 'Abstract Idea: Organize Images' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/15-1372.opinion.5-12-2016.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="text-sm space-y-4">
          <p>
            The Federal Circuit affirmed the dismissal of TLI’s patent claims under § 101, finding them directed to the abstract idea of organizing and storing digital images. Although the claims included physical components like a “telephone unit” and “server,” the court held they performed no more than generic functions.
          </p>
          <p>
            The invention involved capturing images on a mobile device, assigning classification data, transmitting the image and metadata to a server, and storing it according to that classification. The court found no improvement to computer functionality.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Image Classification Patents',
          numbers: 'U.S. Patent No. 8,096,495 (claim 17 representative)',
          description: 'Claims directed to capturing, transmitting, classifying, and storing images in an organized fashion.',
          claims: [
            'Recording a digital image on a device',
            'Transmitting image to a separate storage device',
            'Classifying and storing the image based on its classification'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims are directed to the abstract idea of <strong>classifying and storing digital images</strong> in an organized manner, not a technological improvement.</li>
              <li>• The use of a mobile phone and server is generic and does not transform the idea into a patent‑eligible invention.</li>
            </ul>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• The components perform only well‑understood, routine, and conventional functions.</li>
              <li>• No specific implementation or technical solution is recited to improve image processing or storage technology.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        '"Claims are directed to the abstract idea of taking, organizing, classifying, and storing photographs."',
        '"The recited physical components merely provide a generic environment in which to carry out the abstract idea."'
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">Drafting Tips</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Avoid broadly claiming abstract tasks like organizing digital content.</li>
              <li>Include technical specifics on how storage, retrieval, or classification is improved over the prior art.</li>
              <li>Claim functional improvements to computer operation—not just automation of manual steps.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            <strong>In re TLI</strong> is a foundational case that reinforces the line between abstract ideas and technological inventions.
            It remains a go‑to citation for invalidating claims involving digital content organization without technical detail.
          </p>
          <p>
            Practitioners must be wary of functional claiming and ensure the invention includes an implementation that improves technology—not just digitizes preexisting processes.
          </p>
        </div>
      )
    }
  ]
}

export const yuAppleCaseDetail: CaseDetail = {
  id: 'fed_cir-2020-1760',
  title: 'Yu v. Apple (2021)',
  courtInfo: {
    subtitle: 'Federal Circuit • Jun 11, 2021',
    citation: 'Yu v. Apple Inc., 35 F.4th ___ (Fed. Cir. June 11, 2021)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Algorithm' },
    { type: 'warning', text: 'Dual‑Camera Image Processing' },
    { type: 'guidance', text: 'Abstract Idea: Photo Enhancement' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/20-1760.opinion.6-11-2021_1789244.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            The Federal Circuit affirmed dismissal under Rule 12(b)(6), holding that the claim recites the abstract idea of taking two images and using one to enhance the other—a practice known for over a century in photography.
          </p>
          <p className="text-sm">
            The asserted claim involves a digital camera architecture with dual sensors and lenses, conventional A/D conversion, memory, and software-based image combination—none of which add technical transformation beyond the abstract idea.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Digital Camera Patent',
          numbers: 'U.S. Patent No. 6,611,289 (claim 1)',
          description: 'Camera with two image sensors and lenses, analog-to-digital conversion, memory, and processor that enhances one image with another.',
          claims: [
            'Two image sensors and lenses producing two digital images',
            'Storing both images',
            'Digital processor producing a resultant image enhanced with the second image'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claim is directed to the abstract idea of <strong>taking two images and using one to enhance the other</strong>.</li>
              <li>• This photographic practice has existed for over a century and is neither new nor technical.</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: No Inventive Concept
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• The claim recites well-known, generic camera components doing routine functions.</li>
              <li>• Configuration is described at a high level and fails to transform the abstract idea into patent-eligible subject matter.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"The abstract idea of taking two pictures and using one to enhance the other."`,
        `"Photographers have been doing this for over a century."`,
        `"All recited components were well‑known, routine, and conventional."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <ul className="list-disc ml-6 space-y-2">
            <li>To pass §101, claims must include more than novel combinations of long-standing abstract practices.</li>
            <li>Include technical detail—e.g., new sensor arrangements with performance improvements.</li>
            <li>Specific implementation/configuration matters—don’t rely on generic claims invoking known hardware.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm">
          <p>The decision reinforces Federal Circuit’s tightening of §101 post-Alice: even a physical device with known components can be ineligible if it merely implements an old abstract idea in a high-level, generic environment.</p>
          <p>It creates uncertainty for claims directed to hardware enhancements unless coupled with specific, novel technical features.</p>
        </div>
      )
    }
  ]
}
export const berkheimerCaseDetail: CaseDetail = {
  id: 'fed_cir-2017-1437',
  title: 'Berkheimer v. HP (2018)',
  courtInfo: {
    subtitle: 'Federal Circuit • Feb 8, 2018',
    citation: 'Berkheimer v. HP Inc., 881 F.3d 1360 (Fed. Cir. Feb. 8, 2018)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Routine/Conventional = Fact' },
    { type: 'guidance', text: 'Factual Dispute Required' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/17-1437.order.5-25-2018.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            Berkheimer sued HP over a digital asset archiving system that parsed files into object components,
            tagged them, stored links, and enabled one-to-many editing—allegedly reducing redundancy and improving
            computer efficiency. The district court granted summary judgment that the claims were patent-ineligible
            under § 101. The Federal Circuit affirmed in part but held that some claims (4–7) required factual
            inquiry into unconventional elements, vacating summary judgment for those claims while affirming it
            for others.          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Digital Asset Archive Patent',
          numbers: 'U.S. Patent No. 7,447,713 (claims 1–7, 9)',
          description: 'System parsing digital files into tagged objects, archiving linked object structures for redundancy reduction and one-to-many editing.',
          claims: [
            'Parser breaks files into searchable, tagged objects',
            'Compares objects to archived structures to detect variance',
            'Enables one-to-many editing of linked object structures'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims recited parsing, comparing, storing, and editing data—an abstract idea similar to prior cases. </li>
              <li>• Claim limitations focused on data organization activities, not a technical improvement. </li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: Inventive Concept Investigation
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Claims 1–3 and 9 lacked any inventive concept and were invalidated at summary judgment. </li>
              <li>• Claims 4–7 recited one-to-many editing and redundancy reduction—potential technical improvements identified in the specification. </li>
              <li>• Federal Circuit held that whether these elements are well-understood and conventional is a factual question, precluding summary judgment. </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"Well‑understood, routine, and conventional" computer functions are questions of fact—summary judgment inappropriate on this alone.`,
        `"Claims 4‑7 include limitations arguably unconventional; factual dispute precludes SJ."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <ul className="list-disc ml-6 space-y-2">
            <li>Include claim limitations that clearly describe technological improvements over existing systems.</li>
            <li>Highlight unconventional elements in both specification and claim language to create factual disputes.</li>
            <li>Use expert testimony at the summary-judgment stage to challenge assertions of conventionality.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            Berkheimer reinforced that patent eligibility can hinge on underlying factual disputes—particularly
            whether claimed elements were well-understood, routine, and conventional. It created a pathway to
            resist § 101 challenges via specification-backed technical claims and affirmed eligibility uncertainty
            at the summary-judgment stage.          </p>
        </div>
      )
    }
  ]
}

export const aatrixCaseDetail: CaseDetail = {
  id: 'fed_cir-2017-2437',
  title: 'Aatrix Software, Inc. v. Green Shades Software, Inc.',
  courtInfo: {
    subtitle: 'Federal Circuit • Feb 14, 2018',
    citation: 'Aatrix Software, Inc. v. Green Shades Software, Inc., 882 F.3d 1121 (Fed. Cir. Feb 14, 2018)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Factual Issues on Conventionality' },
    { type: 'guidance', text: 'Factual Dispute Required' }
  ],
  pdfLink: 'https://www.cafc.uscourts.gov/opinions-orders/17-1452.opinion.2-12-2018.1.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4">
          <p className="text-sm">
            Aatrix sued Green Shades alleging infringement of patents covering software that imports data files from external applications into electronic forms, automatically mapping and populating form fields—an ostensibly more efficient and interoperable solution. The district court granted summary judgment, finding the claims patent-ineligible under § 101. The Federal Circuit reversed, finding genuine factual issues about whether asserted elements were well-known and conventional, meaning eligibility could not be decided at summary judgment.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Electronic Form Data-File Patents',
          numbers: 'U.S. Patent Nos. 7,171,615 & 8,984,393',
          description: 'Systems that import data files from third-party applications into electronic forms, improving interoperability and reducing manual entry.',
          claims: [
            'Data file imports information into electronic form fields',
            'Populating form fields without manual input',
            'Improved interoperability with external data sources'
          ]
        }
      ]
    },
    {
      id: 'analysis',
      title: 'Alice Analysis',
      icon: Scale,
      contentType: 'html',
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 1: Directed to Abstract Idea
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• The claimed idea—importing and populating form data—is a longstanding commercial practice and considered abstract.</li>
              <li>• There is no specific technological improvement—i.e., no tweak to how computers process or store data.</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Step 2: Inventive Concept?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Aatrix argued the specific mapping and field population steps are non-conventional—but this remains disputed factually.</li>
              <li>• The Court held that whether these elements were "well‑understood, routine, and conventional" depends on the factual record—summary judgment was inappropriate.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <ul className="list-disc ml-6 space-y-2">
            <li>Include claim language that emphasizes unconventional steps or mappings that improve technology.</li>
            <li>Develop a strong factual record showing the claimed features are not generic or routine.</li>
            <li>Avoid generic form‑filling language; instead, tie claim terms to technical implementation details.</li>
          </ul>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            Aatrix reaffirmed **Berkheimer**: when key elements of a claim raise factual questions about conventionality, patent eligibility under § 101 must be resolved through factual development—not on summary judgment. It reinforced the strategy of including technical detail in claims and evidentiary support early in prosecution and litigation.
          </p>
        </div>
      )
    }
  ]
}

export const aliceCaseDetail: CaseDetail = {
  id: '573_13-298',
  title: 'Alice Corp. v. CLS Bank (2014)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Jun 19, 2014',
    citation: 'Alice Corp. v. CLS Bank Int\'l, 573 U.S. 208 (2014)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Business Method' },
    { type: 'warning', text: 'Intermediated Settlement' },
    { type: 'guidance', text: 'Generic Computer ≠ Inventive' }
  ],
  pdfLink: 'https://supreme.justia.com/cases/federal/us/573/13-298/case.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: 'Gavel',
      contentType: 'html',
      content: (<><p>Alice Corporation held patents for a computerized system designed to mitigate "settlement risk" in financial transactions - the risk that only one party to an agreed-upon exchange will satisfy its obligation. The patents claimed methods for using a third-party computer system as an intermediary to facilitate exchanges between parties.</p>

      <p>CLS Bank, which operates a global currency transaction network, challenged the patents as invalid. The case reached the Supreme Court, which unanimously ruled that the patents were ineligible for protection under 35 U.S.C. § 101.</p>

      <p>This landmark decision established a crucial precedent for patent eligibility of computer-implemented inventions, particularly in the financial technology sector.</p></>)
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: 'FileText',
      contentType: 'html',
      content: (<><p><strong>Patents Involved:</strong> U.S. Patent Nos. 5,970,479; 6,912,510; 7,149,720; and 7,725,375</p>

      <p><strong>Core Technology:</strong> The patents claimed three types of implementations:</p>
      <ul>
        <li><strong>Method claims:</strong> Steps for exchanging financial obligations using a computer intermediary</li>
        <li><strong>System claims:</strong> Computer hardware configured to perform the settlement method</li>
        <li><strong>Media claims:</strong> Computer-readable medium containing program code for the method</li>
      </ul>

      <p><strong>Technical Process:</strong> The system creates "shadow" credit and debit records that mirror parties' real accounts, updates these records in real-time, and only permits transactions where parties have sufficient resources. At day's end, it instructs financial institutions to execute the permitted transactions.</p></>)
    },
    {
      id: 'admissions',
      title: 'Case Insights',
      icon: 'AlertTriangle',
      contentType: 'quotes',
      content: [
        "The claims at issue are directed to a patent-ineligible concept: the abstract idea of intermediated settlement.",
        "Merely requiring generic computer implementation fails to transform that abstract idea into a patent-eligible invention.",
        "Stating an abstract idea 'while adding the words apply it with a computer' simply combines those two steps, with the same deficient result.",
        "The method claims do not, for example, purport to improve the functioning of the computer itself or effect an improvement in any other technology or technical field.",
        "Taking the claim elements separately, the function performed by the computer at each step is 'purely conventional.'"
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: 'Lightbulb',
      contentType: 'html',
      content: (<><p><strong>The Alice Two-Step Test:</strong></p>
      <ol>
        <li><strong>Step 1:</strong> Determine if the claims are directed to a patent-ineligible concept (law of nature, natural phenomenon, or abstract idea)</li>
        <li><strong>Step 2:</strong> If yes, examine whether the claim elements contain an "inventive concept" sufficient to transform the abstract idea into patent-eligible subject matter</li>
      </ol>

      <p><strong>Key Takeaways for Patent Prosecution:</strong></p>
      <ul>
        <li>Generic computer implementation alone cannot save an abstract idea from ineligibility</li>
        <li>Claims must show technological improvement or solve a technical problem</li>
        <li>Avoid merely adding "apply it with a computer" to abstract business methods</li>
        <li>Focus on specific technical improvements rather than conventional computer functions</li>
        <li>Consider how the invention improves computer functionality or other technology</li>
      </ul></>)
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: 'BookOpen',
      contentType: 'html',
      content: (<><p><strong>Immediate Impact:</strong> The Alice decision significantly tightened patent eligibility standards, particularly affecting business method and software patents. It established that simply implementing an abstract idea on a computer is insufficient for patent protection.</p>

      <p><strong>Long-term Consequences:</strong></p>
      <ul>
        <li>Dramatically increased § 101 rejections at the USPTO</li>
        <li>Led to invalidation of thousands of existing patents</li>
        <li>Reshaped patent prosecution strategies for software and business methods</li>
        <li>Created ongoing uncertainty in patent eligibility determinations</li>
      </ul>

      <p><strong>Industries Most Affected:</strong> Financial technology, e-commerce, business software, and computer-implemented business methods saw the greatest impact from this decision.</p>

      <p><strong>Continuing Legacy:</strong> Alice remains the controlling precedent for patent eligibility analysis, though its application continues to evolve through Federal Circuit decisions and USPTO guidance.</p></>)
    }
  ]
}

export const mayoCaseDetail: CaseDetail = {
  id: '566_10-1150',
  title: 'Mayo Collaborative Services v. Prometheus Laboratories, Inc. (2012)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • March 20, 2012',
    citation: 'Mayo Collaborative Services v. Prometheus Laboratories, Inc., 566 U.S. 66 (2012)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Biotechnology' },
    { type: 'warning', text: 'Law of Nature + Routine Steps' },
    { type: 'guidance', text: 'Routine Activity ≠ Inventive Concept' }
  ],
  pdfLink: 'https://supreme.justia.com/cases/federal/us/566/10-1150/case.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court unanimously held that claims directed to a method of giving a drug to a patient,
            measuring metabolites of that drug, and deciding whether to increase or decrease the dosage based
            on known thresholds, were not patent-eligible subject matter under 35 U.S.C. § 101.
          </p>
          <p>
            Thiopurine drugs are used to treat autoimmune disorders and acute leukemia. When ingested, the body
            metabolizes thiopurine compounds differently among individuals, making it difficult to determine
            optimal dosing—too high risks harmful side effects, too low is ineffective.
          </p>
          <p>
            Prometheus developed and patented diagnostic tests based on correlations between metabolite levels
            and drug efficacy/toxicity. Mayo initially purchased these tests but later developed its own
            competing test, leading to Prometheus's infringement suit.
          </p>
          <p>
            The Court found that the patents merely claimed laws of nature with conventional steps added,
            and thus risked preempting future use of natural correlations in medical treatment.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'U.S. Patent Nos. 6,355,623 and 6,680,302',
          numbers: '6,355,623 • 6,680,302',
          description: 'Methods for optimizing therapeutic efficacy of thiopurine drugs used in treating immune-mediated gastrointestinal disorders.',
          claims: [
            'Administering a thiopurine drug to a patient',
            'Determining the level of 6-thioguanine (6-TG) or 6-methyl-mercaptopurine (6-MMP) metabolites in the blood',
            'Comparing metabolite levels to predetermined thresholds',
            'Indicating a need to increase or decrease drug dosage based on the comparison'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"Laws of nature, natural phenomena, and abstract ideas are not patentable."`,
        `"The steps in the claimed processes involve well‑understood, routine, conventional activity previously engaged in by researchers in the field."`,
        `"To transform a law of nature into a patent-eligible application of such a law, one must do more than simply state the law of nature while adding the words 'apply it.'"`,
        `"Simply appending conventional steps, specified at a high level of generality, to laws of nature, natural phenomena, and abstract ideas cannot make those laws, phenomena, and ideas patentable."`,
        `"The additional steps must amount to significantly more than the natural law itself."`,
        `"Patent protection must not impede future discovery by tying up the future use of laws of nature."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Mayo significantly impacts diagnostic method patents. Post-Mayo, 86.4% of office actions include §101 rejections
            for diagnostic methods (compared to 15.9% pre-Mayo). The following strategies can help navigate this challenging landscape.
          </p>

          <h3 className="font-semibold text-gray-900">Drafting Strategies for Patent Eligibility</h3>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Focus on Actions, Not Correlations</p>
            <p className="text-sm">Claims should focus on concrete actions rather than mere correlations between natural products
            and disease states. The correlation itself, no matter how surprising a discovery, is unlikely to confer patent eligibility.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Reframe as Treatment Methods</p>
            <p className="text-sm">Structure claims as therapeutic methods that apply a diagnostic result. For example, claim
            a method of treating disease X with therapeutic Y based upon diagnostic finding Z. Treatment methods involving
            physical administration of therapeutics are patent-eligible.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Include Novel Tools or Reagents</p>
            <p className="text-sm">If detection of a marker requires a new tool (e.g., a novel fluorescent probe or unconventional
            antibody), the use of that non-routine tool in the method may be sufficient to render it patent-eligible.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Consider Apparatus Claims</p>
            <p className="text-sm">Draft claims to a device, system, or apparatus—even if components are not new themselves but
            are used to perform a novel step or calculation. Claims to instruments more specific than general-purpose computers
            are more likely to be found patent-eligible.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="font-semibold mb-2">Key Requirement: "Significantly More"</p>
            <p className="text-sm">Claims must provide "significantly more" than the natural law itself. Highlight unconventional,
            non-routine steps during drafting and prosecution. Well-understood, routine, conventional activity does not add
            "significantly more."</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Mayo</strong> established the framework now used in all § 101 analyses. The Mayo/Alice
            two-step test has become the primary analytical tool for determining patent eligibility of claims
            involving laws of nature, natural phenomena, and abstract ideas.
          </p>
          <p>
            The decision significantly impacts diagnostic method patents, personalized medicine claims, and
            any inventions that rely on natural correlations or observations. Many biotechnology and
            pharmaceutical diagnostic patents have been invalidated under this framework.
          </p>
          <p>
            The Court's emphasis on "inventive concept" and "significantly more" has led to increased
            scrutiny of claims that combine natural laws with conventional steps, particularly affecting:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Medical diagnostic methods</li>
            <li>Genetic testing and analysis</li>
            <li>Precision medicine and treatment optimization</li>
            <li>Methods of medical treatment based on natural correlations</li>
          </ul>
        </div>
      )
    }
  ]
}
export const bilskiCaseDetail: CaseDetail = {
  id: '561_08-964',
  title: 'Bilski v. Kappos (2010)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Jun 28, 2010',
    citation: 'Bilski v. Kappos, 561 U.S. 593 (2010)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Business Method' },
    { type: 'warning', text: 'Hedging Method = Abstract' },
    { type: 'guidance', text: 'Machine‑or‑Transformation Clue' }
  ],
  pdfLink: 'https://supreme.justia.com/cases/federal/us/561/08-964/case.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court held that Bilski's patent application claims—seeking to patent a method for hedging
            risk in commodity trading—were directed to an unpatentable abstract idea under 35 U.S.C. § 101.
          </p>
          <p>
            The patent application described a series of steps for commodities buyers and sellers in the energy
            market to hedge against the risk of price changes. Claim 1 outlined the hedging steps, and Claim 4
            reduced the concept to a mathematical formula.
          </p>
          <p>
            The patent examiner rejected the application because the invention was not tied to a specific apparatus,
            merely manipulated an abstract idea, and solved a purely mathematical problem. The Board of Patent Appeals
            and Interferences affirmed.
          </p>
          <p>
            Importantly, while rejecting Bilski's claims, the Court held that the machine-or-transformation test
            is not the <em>sole</em> test for patent eligibility—it's merely "a useful and important clue." The Court
            also rejected categorical exclusion of business method patents, holding that at least some business
            methods may be patentable.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent Application Claims',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Serial No. 08/833,892',
          numbers: 'Application 08/833,892',
          description: 'Method for managing the consumption risk costs of a commodity sold by a commodity provider at a fixed price comprising hedging transactions in energy markets.',
          claims: [
            'Claim 1: Initiating a series of transactions between providers and consumers at fixed rates',
            'Claim 4: Reducing the hedging concept to a mathematical formula',
            'Remaining claims: Applications of the hedging concept to energy suppliers and consumers to minimize risks from market demand fluctuations'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"The claimed invention is an unpatentable abstract idea, just like the algorithms at issue in Benson and Flook."`,
        `"Hedging is a fundamental economic practice long prevalent in our system of commerce."`,
        `"Allowing petitioners to patent risk hedging would pre-empt use of this approach in all fields, and would effectively grant a monopoly over an abstract idea."`,
        `"The machine-or-transformation test is not the sole test for deciding whether an invention is a patent-eligible 'process.'"`,
        `"The machine-or-transformation test is a useful and important clue, an investigative tool, for determining whether some claimed inventions are processes under §101."`,
        `"Section 101 similarly precludes the broad contention that the term 'process' categorically excludes business methods."`,
        `"Statements that merely limit the use of an abstract idea to a particular technological environment are not sufficient."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Bilski moderated the strict machine-or-transformation test while providing limited concrete guidance.
            The decision expanded potential eligibility while making "abstract idea" boundaries uncertain.
          </p>

          <h3 className="font-semibold text-gray-900">Key Takeaways for Patent Drafting</h3>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-3">
            <p className="font-semibold mb-2">Machine-or-Transformation Test is Not Exclusive</p>
            <p className="text-sm">Claims may be patent-eligible even if they don't satisfy the machine-or-transformation
            test. This test remains "a useful and important clue" but is not the sole criterion for § 101 eligibility.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Business Methods Are Not Categorically Excluded</p>
            <p className="text-sm">The Court held that § 101 includes the word "method" and thus at least some business
            method claims can constitute patentable subject matter. Business methods are not automatically ineligible.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Emphasize Particular Machine Operations</p>
            <p className="text-sm">When drafting process claims, structure them to emphasize particular machine operations
            or the physical results of claimed processes. Tie claims to specific technological implementations.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Avoid Preempting Abstract Ideas</p>
            <p className="text-sm">Claims that would preempt use of an approach "in all fields" or effectively grant
            a monopoly over an abstract idea will be rejected. Fundamental economic or business practices cannot be
            patented in the abstract.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
            <p className="font-semibold mb-2">⚠ Field-of-Use Limitations Are Insufficient</p>
            <p className="text-sm">Merely limiting an abstract idea to a particular technological environment or field
            of use is not sufficient to confer patent eligibility. Claims must go beyond field-of-use restrictions.</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Bilski</strong> was a pivotal but uncertain ruling that left many questions unanswered. While it
            confirmed that business methods are not categorically excluded, it provided limited guidance on what
            constitutes an "abstract idea."
          </p>
          <p>
            The decision's most significant impact was rejecting the machine-or-transformation test as the sole
            criterion for patent eligibility. This created broader potential eligibility but also uncertainty, as
            the Court did not establish clear alternative tests.
          </p>
          <p>
            <strong>Key Long-Term Effects:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Business Method Patents:</strong> Preserved business method patent eligibility while making
            clear that fundamental economic practices remain unpatentable</li>
            <li><strong>Software Patents:</strong> Created space for software patents that fail machine-or-transformation
            but still involve technical implementations</li>
            <li><strong>Led to Alice:</strong> The limited guidance in Bilski contributed to continued confusion,
            ultimately leading to Alice Corp. v. CLS Bank (2014), which established the two-step framework</li>
            <li><strong>USPTO Guidance:</strong> Prompted interim guidance from USPTO attempting to clarify eligibility
            standards, though significant uncertainty remained</li>
          </ul>
          <p>
            The 5-4 split on business method eligibility reflected deep disagreement on the Court about the proper
            scope of § 101, foreshadowing ongoing patent eligibility debates.
          </p>
        </div>
      )
    }
  ]
}
export const diehrCaseDetail: CaseDetail = {
  id: '450_175',
  title: 'Diamond v. Diehr (1981)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Mar 3, 1981',
    citation: 'Diamond v. Diehr, 450 U.S. 175 (1981)'
  },
  badges: [
    { type: 'eligible', text: '§ 101 Eligible' },
    { type: 'warning', text: 'Algorithm' },
    { type: 'warning', text: 'Hardware' },
    { type: 'innovation', text: 'Industrial Process + Algorithm' },
    { type: 'guidance', text: 'Whole‑Claim Analysis' }
  ],
  pdfLink: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep450/usrep450175/usrep450175.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court held that a process for curing synthetic rubber—which used a well-known mathematical
            equation (the Arrhenius equation) and a computer to continuously calculate optimal cure time—was
            patent-eligible under 35 U.S.C. § 101.
          </p>
          <p>
            The invention addressed a long-standing problem in the rubber molding industry: the difficulty of
            obtaining accurate, constant temperature measurements inside the mold. The process used a thermocouple
            to continuously measure mold temperature, fed those measurements into a computer that repetitively
            calculated cure time using the Arrhenius equation, and automatically opened the press at precisely
            the right moment.
          </p>
          <p>
            The patent examiner rejected the claims as directed to nonstatutory subject matter, reasoning that
            the claims were merely a computer program for calculating cure time. The Board of Patent Appeals and
            Interferences affirmed, but the Court of Customs and Patent Appeals reversed.
          </p>
          <p>
            The Supreme Court affirmed the CCPA, holding that claims must be considered as a whole, not dissected
            into old and new elements. The Court found that the process transformed raw, uncured rubber into a
            cured precision product—a patent-eligible industrial process—and the use of a mathematical formula
            did not render it unpatentable.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent Claims at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Process for Curing Synthetic Rubber',
          numbers: 'U.S. Patent Application',
          description: 'A process for molding raw, uncured synthetic rubber into cured precision products using computer-controlled temperature monitoring and the Arrhenius equation.',
          claims: [
            'Installing rubber in a press mold',
            'Closing the mold',
            'Constantly measuring the temperature in the mold using a thermocouple',
            'Repetitively calculating cure time using the Arrhenius equation: ln v = CZ + x (where v = cure time, C = activation energy constant, Z = temperature, x = constant based on mold geometry)',
            'Automatically opening the press when calculated cure time equals elapsed time'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"A claim drawn to subject matter otherwise statutory does not become nonstatutory simply because it uses a mathematical formula or digital computer."`,
        `"It is inappropriate to dissect the claims into old and new elements and ignore the presence of the old elements in the analysis."`,
        `"When a claim containing a mathematical formula implements or applies that formula in a structure or process which, when considered as a whole, is performing a function which the patent laws were designed to protect, then the claim satisfies the requirements of § 101."`,
        `"A process is not unpatentable simply because it contains a law of nature or a mathematical algorithm."`,
        `"The 'novelty' of any element or steps in a process, or even of the process itself, is of no relevance in determining whether the subject matter of a claim falls within the § 101 categories of possibly patentable subject matter."`,
        `"Transformation and reduction of an article 'to a different state or thing' is the clue to the patentability of a process claim that does not include particular machines."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Diehr established foundational principles for patent eligibility that remain relevant today, particularly
            the requirement to evaluate claims as a whole and the importance of practical application.
          </p>

          <h3 className="font-semibold text-gray-900">Key Principles from Diehr</h3>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Evaluate Claims as a Whole</p>
            <p className="text-sm">Do not dissect claims into old and new elements. The entire claim must be considered
            together. A claim doesn't become ineligible merely because it includes conventional elements or well-known
            mathematical formulas.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Focus on Practical Application and Transformation</p>
            <p className="text-sm">The claims were patent-eligible because they transformed raw rubber into a cured
            product—a physical transformation solving a real-world industrial problem. Emphasize how your process
            transforms physical materials or improves existing technological processes.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Integrate Algorithms into Industrial Processes</p>
            <p className="text-sm">The Arrhenius equation was patent-eligible when integrated into a complete industrial
            process with physical steps (installing rubber, closing mold, measuring temperature, opening press).
            Embed mathematical formulas within concrete process steps that produce tangible results.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-3">
            <p className="font-semibold mb-2">Solve Technological Problems</p>
            <p className="text-sm">The claims used a well-known equation but applied it to solve a technological
            problem in conventional industry practice—obtaining accurate constant temperature measurements. Show how
            your invention addresses technical challenges in established fields.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="font-semibold mb-2">Computer Implementation Doesn't Preclude Eligibility</p>
            <p className="text-sm">The mere presence of a computer or software element does not make an otherwise
            patent-eligible process ineligible. If the process as a whole transforms materials or performs a statutory
            function, computer implementation is acceptable.</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Diamond v. Diehr</strong> remains one of the most important patent eligibility cases, establishing
            the "considered as a whole" doctrine and the transformation test that courts still apply today.
          </p>
          <p>
            The decision created a critical distinction: mathematical formulas in the abstract are not patentable,
            but practical applications of formulas within industrial processes that transform physical materials are
            patent-eligible. This principle has been repeatedly cited in modern § 101 jurisprudence.
          </p>
          <p>
            <strong>Lasting Impact on Patent Law:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Whole Claim Analysis:</strong> Established that claims cannot be dissected into old and new
            elements—examiners must consider the claim as an integrated whole</li>
            <li><strong>Computer-Implemented Inventions:</strong> Opened the door for computer-controlled processes and
            software-implemented methods, as long as they serve practical purposes and transform physical materials</li>
            <li><strong>Transformation Test:</strong> Contributed to developing the machine-or-transformation test,
            with its emphasis on transforming articles "to a different state or thing"</li>
            <li><strong>Industrial Process Patents:</strong> Affirmed that traditional industrial processes using
            modern technology (computers, sensors) remain patent-eligible</li>
            <li><strong>§ 101 vs. §§ 102/103:</strong> Clarified that novelty and non-obviousness are separate
            inquiries from subject matter eligibility under § 101</li>
          </ul>
          <p>
            <strong>Modern Relevance:</strong> Diehr is frequently cited as a positive example in § 101 analyses.
            When claims integrate abstract ideas or algorithms into practical applications that transform physical
            materials or improve technological processes, Diehr provides the framework for demonstrating eligibility.
          </p>
        </div>
      )
    }
  ]
}
export const bensonCaseDetail: CaseDetail = {
  id: '409_63',
  title: 'Gottschalk v. Benson (1972)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Nov 20, 1972',
    citation: 'Gottschalk v. Benson, 409 U.S. 63 (1972)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Algorithm' },
    { type: 'warning', text: 'Software' },
    { type: 'warning', text: 'Algorithm Alone = Abstract' },
    { type: 'guidance', text: 'Preemption Concerns' }
  ],
  pdfLink: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep409/usrep409063/usrep409063.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court held that a patent application claiming a method for converting binary-coded
            decimal (BCD) numerals into pure binary numerals was not patent-eligible subject matter under
            35 U.S.C. § 101 because it was directed to a mathematical algorithm in the abstract.
          </p>
          <p>
            The patent application, filed by Gary Benson and Arthur Tabbot, described a process for converting
            numbers from BCD notation (where each decimal digit is represented by four binary digits) into pure
            binary form. For example, the decimal number 53 is represented as 0101 0011 in BCD, but 110101 in
            pure binary. The conversion algorithm could be performed mentally, on paper, or by a general-purpose
            digital computer.
          </p>
          <p>
            The patent examiner rejected the claims, and the Board of Patent Appeals affirmed. The Court of
            Customs and Patent Appeals (CCPA) reversed, holding the claims were patent-eligible. The Supreme
            Court reversed the CCPA, finding that the patent would "wholly pre-empt the mathematical formula
            and in practical effect would be a patent on the algorithm itself."
          </p>
          <p>
            This was the first major Supreme Court case addressing computer software patentability and established
            foundational principles about mathematical algorithms, preemption, and abstract ideas that continue
            to influence patent law today.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent Application Claims',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Method for Converting BCD to Binary',
          numbers: 'Patent Application',
          description: 'A method for converting binary-coded decimal (BCD) numerals into pure binary numerals using mathematical operations that could be performed on any general-purpose digital computer.',
          claims: [
            'Claim 8: A 7-step method involving shifting binary digits, masking certain positions, and adding binary values',
            'Claim 13: A data processing method for testing and manipulating binary digit positions',
            'Claims not limited to any particular computer, machinery, or end use',
            'Claims covered the algorithm in any general-purpose digital computer'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"The patent would wholly preempt the mathematical formula and, in practical effect, would be a patent on the algorithm itself."`,
        `"It is conceded that one may not patent an idea. But in practical effect that would be the result if the formula for converting BCD numerals to pure binary numerals were patented in this case."`,
        `"A principle, in the abstract, is a fundamental truth; an original cause; a motive; these cannot be patented, as no one can claim in either of them an exclusive right."`,
        `"Phenomena of nature, though just discovered, mental processes, and abstract intellectual concepts are not patentable, as they are the basic tools of scientific and technological work."`,
        `"The claims were so abstract and sweeping as to cover both known and unknown uses of the BCD to pure binary conversion."`,
        `"The mathematical formula involved here has no substantial practical application except in connection with a digital computer."`,
        `"We are not freezing process patents to old technologies, leaving no room for the revelations of the new, onrushing technology."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Benson established the foundational principle that mathematical algorithms and abstract ideas are not
            patentable when they would preempt all uses of the underlying concept. This case remains the starting
            point for understanding software patent limitations.
          </p>

          <h3 className="font-semibold text-gray-900">Key Principles from Benson</h3>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Avoid Preempting Entire Fields of Use</p>
            <p className="text-sm">The fatal flaw in Benson's claims was that they would preempt all uses of the
            BCD-to-binary conversion algorithm—both known and unknown, in any field, on any computer. Claims must
            be narrower than "all possible applications" of an algorithm.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Mathematical Algorithms Standing Alone Are Not Patentable</p>
            <p className="text-sm">An algorithm that can be performed mentally, on paper, or by computer without
            any transformation of physical materials or connection to specific machinery is not patent-eligible.
            "Basic tools of scientific and technological work" cannot be monopolized.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Tie Algorithms to Specific Machines or Transformations</p>
            <p className="text-sm">The Court noted the claims were "not limited to any particular art or technology,
            to any particular apparatus or machinery, or to any particular end use." To avoid this problem, limit
            claims to specific implementations, particular machinery, or transformative processes.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Show Practical Application Beyond the Algorithm</p>
            <p className="text-sm">Demonstrate concrete, practical applications of the algorithm that go beyond
            simply implementing the mathematical formula. Connect the algorithm to solving specific technological
            problems in particular fields.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="font-semibold mb-2">The Court Left Room for Software Patents</p>
            <p className="text-sm">The Court explicitly stated it was "not freezing process patents to old technologies."
            Software and algorithms can be patent-eligible when properly integrated into specific applications,
            tied to particular machines, or used to transform physical materials.</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Gottschalk v. Benson</strong> was the first Supreme Court case to address computer software
            patentability and established foundational § 101 principles that continue to shape patent law over
            50 years later.
          </p>
          <p>
            The decision created the <strong>preemption doctrine</strong>—the principle that patents cannot wholly
            preempt all uses of abstract ideas, mathematical formulas, or algorithms. This doctrine has been cited
            in virtually every major patent eligibility case since, including <em>Mayo</em>, <em>Alice</em>, and
            their progeny.
          </p>
          <p>
            <strong>Historical Context and Long-Term Impact:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Software Patent Uncertainty:</strong> Created decades of uncertainty about software patentability,
            with the USPTO and courts struggling to define when software implementations cross from abstract to concrete</li>
            <li><strong>Machine-or-Transformation Origins:</strong> The Court's discussion of "particular apparatus
            or machinery" and transformation laid groundwork for the later machine-or-transformation test</li>
            <li><strong>Abstract Idea Jurisprudence:</strong> Established that "mental processes and abstract
            intellectual concepts" are fundamental building blocks that cannot be monopolized</li>
            <li><strong>Ongoing Relevance:</strong> Despite being 50+ years old, Benson is still regularly cited
            in modern § 101 rejections, particularly for pure software or algorithm claims</li>
            <li><strong>Congressional Inaction:</strong> The Court invited Congress to address software patent
            policy, but Congress has never provided specific statutory guidance</li>
          </ul>
          <p>
            <strong>Modern Application:</strong> Benson serves as a cautionary example. When claims are "so abstract
            and sweeping" that they would cover all uses of an algorithm or mathematical concept, they will be
            rejected. Patent applicants must show their claims add "something more" beyond the abstract idea itself—
            a principle later formalized in the Alice/Mayo framework.
          </p>
        </div>
      )
    }
  ]
}
export const flookCaseDetail: CaseDetail = {
  id: '437_584',
  title: 'Parker v. Flook (1978)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Jun 22, 1978',
    citation: 'Parker v. Flook, 437 U.S. 584 (1978)'
  },
  badges: [
    { type: 'ineligible', text: '§ 101 Ineligible' },
    { type: 'warning', text: 'Algorithm' },
    { type: 'warning', text: 'Alarm-Limit Formula' },
    { type: 'guidance', text: 'Post‑Solution = Conventional' }
  ],
  pdfLink: 'https://tile.loc.gov/storage-services/service/ll/usrep/usrep437/usrep437584/usrep437584.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court held that a patent application for a "Method for Updating Alarm Limits" in catalytic
            chemical conversion processes was not patent-eligible under 35 U.S.C. § 101 because the only novel
            feature was a mathematical algorithm, and the conventional pre- and post-solution steps did not add
            patentable subject matter.
          </p>
          <p>
            Dale Flook's patent application described a method for updating alarm limits during catalytic conversion
            of hydrocarbons. In these processes, operating conditions (temperature, pressure, flow rates) are
            monitored, and alarms sound when variables exceed preset limits. Flook's method used a mathematical
            formula to automatically recalculate and update these alarm limits as conditions changed.
          </p>
          <p>
            The three-step process was: (1) measure current values of process variables, (2) use a mathematical
            algorithm to calculate an updated alarm limit, and (3) adjust the alarm system to the new limit.
            The patent examiner rejected the application, and the Board of Patent Appeals affirmed. The Court of
            Customs and Patent Appeals reversed, but the Supreme Court reversed the CCPA.
          </p>
          <p>
            This case established critical principles about "post-solution activity" and "insignificant
            extra-solution activity"—conventional steps added before or after applying an algorithm cannot
            transform an unpatentable principle into a patentable process. Flook sits between <em>Benson</em>
            (1972) and <em>Diehr</em> (1981) as the second case in the algorithm patentability trilogy.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patent Application Claims',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'Method for Updating Alarm Limits',
          numbers: 'Patent Application',
          description: 'A method for updating alarm limits during catalytic conversion of hydrocarbons, wherein the only novel element is a mathematical formula for calculating updated alarm limit values.',
          claims: [
            'Step 1: Measure at least one current value of a process variable (e.g., temperature, pressure, flow rate)',
            'Step 2: Use a mathematical algorithm to calculate an updated alarm-limit value based on the measured variable',
            'Step 3: Adjust the alarm system to reflect the newly calculated alarm-limit value',
            'Claims limited to catalytic conversion processes (a field-of-use limitation)',
            'Algorithm itself was the only novel feature; pre- and post-solution steps were conventional'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"The notion that post-solution activity, no matter how conventional or obvious in itself, can transform an unpatentable principle into a patentable process exalts form over substance."`,
        `"A competent draftsman could attach some form of post-solution activity to almost any mathematical formula."`,
        `"The process itself, not merely the mathematical algorithm, must be new and useful."`,
        `"Once that algorithm is assumed to be within the prior art, the application, considered as a whole, contains no patentable invention."`,
        `"The only difference between the conventional methods of changing alarm limits and that described in respondent's application rests in the second step—the mathematical algorithm or formula."`,
        `"Flook's method is unpatentable under § 101, not because it contains a mathematical algorithm as one component, but because once that algorithm is assumed to be within the prior art, the application contains no patentable invention."`,
        `"Even a novel and useful mathematical formula or scientific principle may not be patented if the only way to make use of it is through a computer program."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Flook established that adding conventional "pre-solution" or "post-solution" activity around an
            algorithm does not make it patent-eligible. This principle directly influenced the modern Mayo/Alice
            framework's requirement for "significantly more."
          </p>

          <h3 className="font-semibold text-gray-900">Key Principles from Flook</h3>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Post-Solution Activity Does Not Save Unpatentable Algorithms</p>
            <p className="text-sm">Adding conventional steps after applying a mathematical formula—like Flook's
            "adjust the alarm limit"—cannot transform an unpatentable principle into patentable subject matter.
            As the Court warned, competent drafters could attach post-solution activity to any formula, including
            the Pythagorean theorem.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Field-of-Use Limitations Are Insufficient</p>
            <p className="text-sm">Flook's claims were limited to catalytic conversion processes, yet this field-of-use
            limitation did not confer eligibility. Merely restricting an algorithm to a particular technological
            field does not make it patentable if the implementation is conventional.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ The Entire Process Must Be Novel, Not Just the Algorithm</p>
            <p className="text-sm">The Court held that "the process itself, not merely the mathematical algorithm,
            must be new and useful." When you treat the algorithm as if it were already in the prior art, what
            remains must still constitute a patentable invention.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Ensure Non-Conventional Implementation Steps</p>
            <p className="text-sm">Unlike Flook's conventional measurement and adjustment steps, claims should
            include unconventional, non-obvious implementation steps that go beyond routine data gathering and
            conventional application of results. The steps surrounding the algorithm must add significant value.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="font-semibold mb-2">Controversial Decision with Limited Scope</p>
            <p className="text-sm">Flook has been extensively criticized and is often distinguished in favorable
            decisions. Diamond v. Diehr (1981) limited Flook's impact by emphasizing whole-claim analysis. However,
            the "insignificant extra-solution activity" concept remains important in modern § 101 analysis.</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Parker v. Flook</strong> represents the middle case in the Supreme Court's algorithm
            patentability trilogy (Benson-Flook-Diehr) and remains one of the most controversial patent law
            decisions. The case established principles about insignificant extra-solution activity that continue
            to influence § 101 analysis today.
          </p>
          <p>
            The decision introduced the analytical approach of treating the algorithm "as if it were in the prior
            art" to determine if anything patentable remains—a precursor to the modern Mayo/Alice Step 2 analysis
            asking whether claims add "significantly more."
          </p>
          <p>
            <strong>Key Impact on Patent Law:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Post-Solution Activity Doctrine:</strong> Established that adding conventional steps after
            applying an algorithm cannot confer patent eligibility—a principle directly incorporated into Mayo's
            "significantly more" analysis</li>
            <li><strong>Field-of-Use Limitations:</strong> Confirmed that merely limiting an algorithm's use to
            a particular field or technology is insufficient for patent eligibility</li>
            <li><strong>Treat Algorithm as Prior Art:</strong> Introduced the analytical method of assuming the
            algorithm is known, then determining if the remaining elements constitute a patentable invention</li>
            <li><strong>Criticism and Controversy:</strong> Judge Giles Rich extensively criticized Justice Stevens's
            opinion, arguing it improperly imported §§ 102/103 criteria into § 101 analysis</li>
            <li><strong>Limited by Diehr:</strong> Three years later, Diamond v. Diehr emphasized whole-claim
            analysis and limited Flook's scope, but didn't overturn its core principles</li>
          </ul>
          <p>
            <strong>Modern Application:</strong> Flook's legacy lives on in the Mayo/Alice framework. When patent
            examiners or courts find that claims merely add "well-understood, routine, conventional activity"
            to an abstract idea, they are applying Flook's post-solution activity doctrine. The case serves as
            a warning that conventional "bookend" steps (data gathering before, routine application after) cannot
            rescue otherwise ineligible claims.
          </p>
        </div>
      )
    }
  ]
}

export const myriadCaseDetail: CaseDetail = {
  id: '569_576',
  title: 'Association for Molecular Pathology v. Myriad Genetics, Inc. (2013)',
  courtInfo: {
    subtitle: 'U.S. Supreme Court • Jun 13, 2013',
    citation: 'Assoc. for Molecular Pathology v. Myriad Genetics, Inc., 569 U.S. 576 (2013)'
  },
  badges: [
    { type: 'ineligible', text: 'Isolated DNA Ineligible' },
    { type: 'warning', text: 'Biotechnology' },
    { type: 'eligible', text: 'cDNA Eligible' },
    { type: 'guidance', text: 'Product of Nature Rule' }
  ],
  pdfLink: 'https://supreme.justia.com/cases/federal/us/569/12-398/case.pdf',
  sections: [
    {
      id: 'overview',
      title: 'Case Overview',
      icon: Gavel,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            The Supreme Court unanimously held that naturally occurring DNA sequences, even when isolated from
            the human genome, are not patent-eligible under 35 U.S.C. § 101 because they are products of nature.
            However, the Court held that synthetically created complementary DNA (cDNA) is patent-eligible because
            it is not naturally occurring.
          </p>
          <p>
            Myriad Genetics discovered the precise chromosomal location and sequence of two human genes, BRCA1
            and BRCA2, mutations in which dramatically increase the risk of breast and ovarian cancer. Based on
            this discovery, Myriad obtained patents claiming isolated DNA sequences, cDNA, and methods for
            detecting mutations. Myriad's patents effectively gave it a monopoly on BRCA genetic testing.
          </p>
          <p>
            The Association for Molecular Pathology, along with researchers, genetic counselors, and patients,
            challenged the patents, arguing that genes and genetic information are products of nature and basic
            tools of scientific research that should not be monopolized. The district court invalidated the claims,
            the Federal Circuit reversed in part, and the Supreme Court granted certiorari.
          </p>
          <p>
            Justice Thomas, writing for a unanimous Court, held that Myriad's claims to isolated DNA were not
            patent-eligible because Myriad "did not create anything" but merely found and isolated genes that
            already existed in nature. However, cDNA claims were eligible because cDNA is "reverse engineered"
            from mRNA and does not occur naturally—it lacks introns (non-coding sequences) present in genomic DNA.
          </p>
        </div>
      )
    },
    {
      id: 'patents',
      title: 'Patents at Issue',
      icon: FileText,
      contentType: 'patents',
      content: [
        {
          title: 'BRCA1 and BRCA2 Gene Patents',
          numbers: 'U.S. Patents 5,747,282 • 5,837,492 • 6,033,857 • others',
          description: 'Myriad claimed 24 patents with over 500 claims related to BRCA1 and BRCA2 genes, which are linked to significantly increased risk of breast and ovarian cancer.',
          claims: [
            'Isolated DNA sequences coding for BRCA1 and BRCA2 proteins',
            'Isolated DNA sequences with specific mutations in BRCA genes',
            'Complementary DNA (cDNA) encoding BRCA proteins',
            'Methods for detecting cancer-predisposing mutations',
            'Screening methods for cancer therapeutics'
          ]
        }
      ]
    },
    {
      id: 'admissions',
      title: 'Key Quotes & Legal Standards',
      icon: AlertTriangle,
      contentType: 'quotes',
      content: [
        `"A naturally occurring DNA segment is a product of nature and not patent eligible merely because it has been isolated, but cDNA is patent eligible because it is not naturally occurring."`,
        `"Myriad did not create anything. To be sure, it found an important and useful gene, but separating that gene from its surrounding genetic material is not an act of invention."`,
        `"Groundbreaking, innovative, or even brilliant discovery does not by itself satisfy the § 101 inquiry."`,
        `"Myriad's claims are simply not expressed in terms of chemical composition, nor do they rely in any way on the chemical changes that result from the isolation of a particular section of DNA."`,
        `"It is undisputed that Myriad did not create or alter any of the genetic information encoded in the BRCA1 and BRCA2 genes."`,
        `"The claims are not expressed in terms of chemical composition, nor do they rely on the chemical changes resulting from the isolation of a particular DNA section."`,
        `"cDNA is not a 'product of nature' and is patent eligible... because it is not naturally occurring."`
      ]
    },
    {
      id: 'guidance',
      title: 'Practical Guidance',
      icon: Lightbulb,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p className="text-sm italic mb-4">
            Myriad invalidated thousands of gene patent claims but left most biotechnology intellectual property
            intact. The decision requires focusing on human-made modifications and synthetic constructs rather
            than naturally occurring molecules.
          </p>

          <h3 className="font-semibold text-gray-900">Key Principles from Myriad</h3>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Isolated Natural Products Are Not Patent-Eligible</p>
            <p className="text-sm">Merely isolating, purifying, or extracting a naturally occurring molecule does
            not make it patent-eligible. The isolated molecule must be "markedly different" from what exists in
            nature—having a new or distinctive form, quality, or property beyond simple isolation.</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
            <p className="font-semibold mb-2">⚠ Discovery ≠ Invention</p>
            <p className="text-sm">"Groundbreaking, innovative, or even brilliant discovery does not by itself
            satisfy the § 101 inquiry." Finding something that already exists in nature, no matter how difficult
            or valuable, is not the same as creating something new.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Synthetic and Engineered Molecules Are Patent-Eligible</p>
            <p className="text-sm">cDNA is patent-eligible because it is synthetically created and does not occur
            in nature—it contains only exons (coding sequences) without introns (non-coding sequences) found in
            genomic DNA. Similarly, modified proteins, engineered vectors, and synthetic constructs remain patentable.</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-3">
            <p className="font-semibold mb-2">✓ Focus on Methods, Applications, and Tools</p>
            <p className="text-sm">While isolated genes are unpatentable, methods for detecting mutations, screening
            assays, primers, probes, and diagnostic techniques can still be protected. Myriad retains competitive
            advantages through its mutation database, testing methods, and know-how.</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="font-semibold mb-2">"Markedly Different" Standard</p>
            <p className="text-sm">To be patent-eligible, a claimed composition derived from nature must have
            characteristics "markedly different" from the natural product. This requires demonstrating structural
            or functional differences created through human intervention, not just separation from surrounding material.</p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact & Significance',
      icon: BookOpen,
      contentType: 'html',
      content: (
        <div className="space-y-4 text-gray-700">
          <p>
            <strong>Myriad</strong> fundamentally changed biotechnology patent law by invalidating thousands of
            gene patent claims while preserving most other forms of biotech intellectual property. The decision
            struck a careful balance between promoting innovation and ensuring access to genetic information.
          </p>
          <p>
            The ruling immediately opened the BRCA genetic testing market to competition. Within days of the
            decision, multiple laboratories announced BRCA testing services at significantly lower prices than
            Myriad's tests, benefiting patients seeking cancer risk assessment.
          </p>
          <p>
            <strong>Key Impact Areas:</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Gene Patents:</strong> An estimated 3,535+ unexpired gene sequence patents were potentially
            affected, though many contained other patent-eligible claims (methods, cDNA, etc.)</li>
            <li><strong>Genetic Testing Market:</strong> Opened competition in diagnostic testing for hereditary
            diseases, dramatically reducing costs for patients and expanding access</li>
            <li><strong>Research Freedom:</strong> Eliminated patent barriers to basic genetic research, allowing
            scientists to study genes without fear of infringement</li>
            <li><strong>Biotechnology Innovation:</strong> Despite concerns, the biotech industry remained largely
            unaffected—patents on recombinant proteins, antibodies, modified organisms, and therapeutic methods
            were unaffected</li>
            <li><strong>Products of Nature Doctrine:</strong> Reinforced and clarified the longstanding principle
            that products of nature are not patentable, requiring "markedly different" characteristics</li>
          </ul>
          <p>
            <strong>Limited Scope:</strong> The Court emphasized it was not ruling on the patent-eligibility of
            method claims, new applications of knowledge about genes, or DNA alterations that create different
            characteristics. Biotech companies adapted by focusing on synthetic biology, engineered proteins,
            diagnostic methods, and therapeutic applications rather than isolated natural molecules.
          </p>
        </div>
      )
    }
  ]
}

// Export all case details
export const caseDetailsMap: Record<string, CaseDetail> = {
  '573_13-298': aliceCaseDetail,
  '566_10-1150': mayoCaseDetail,
  '561_08-964': bilskiCaseDetail,
  '450_175': diehrCaseDetail,
  '409-63': bensonCaseDetail,
  '437-584': flookCaseDetail,
  '569_576': myriadCaseDetail,
  'fed_cir-2015-1244': enfishCaseDetail,
  'fed_cir-2015-1080': mcroCaseDetail,
  'fed_cir-2013-1505': ddrCaseDetail,
  'fed_cir-2016-2254': visualMemoryCaseDetail,
  'fed_cir-2016-2520': finjanCaseDetail,
  'fed_cir-2015-5150': thalesCaseDetail,
  'fed_cir-2016-2684': coreWirelessCaseDetail,
  'fed_cir-2023-2437': recentiveCaseDetail,
  'fed_cir-2022-2109': aiVisualizeCaseDetail,
  'fed_cir-2015-1778': electricPowerCaseDetail,
  'fed_cir-2013-1600': digitechCaseDetail,
  'fed_cir-2016-1233': smartSystemsCaseDetail,
  'fed_cir-2018-1739': chargePointCaseDetail,
  'fed_cir-2016-2531': twoWayMediaCaseDetail,
  'fed_cir-2015-1372': tliCommunicationsCaseDetail,
  'fed_cir-2020-1760': yuAppleCaseDetail,
  'fed_cir-2017-1437': berkheimerCaseDetail,
  'fed_cir-2017-2437': aatrixCaseDetail,
};
